import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, CheckCircle, ArrowLeft, Banknote, Eye, Info, Calculator, Ticket, FileText, AlertTriangle, FlaskConical, CalendarClock } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { normalizeMesReferencia } from './FinanceiroLayout';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { Fatura } from '../../types';
import { addDaysISO, formatDateString } from '../../src/lib/janelaCobranca';
import { exigeJustificativaAtraso, houveEmissaoDoBoletoDoMes } from '../../src/lib/janelaFaturamento';
import { competenciaParaData } from '../../src/lib/fechamentoAgrupamento';
import { prazoAgrupamentoDaUc } from '../../src/lib/prazoAgrupamentoDaUc';
import JustificativaAtrasoModal from './JustificativaAtrasoModal';
import LembretesViewer from './components/LembretesViewer';
import { resolveBancoGateway, criarCobrancaAsaas, gerarBoletoGran, emitirBoletoTeste, publicarPdfBoletoVigor } from '../../src/lib/gatewayResolver';
import { emissaoBoletoValida, boletoJaRegistrado, motivoBoletoJaRegistrado, boletoParadoAntesDoEnvio, conferirValorDoBoleto, ETAPA_A_ENVIAR } from '../../src/lib/emissaoBoletoValida';
import { lerBoletoDaFatura, barrarSeTemBoletoVivo, type BoletoLido } from '../../src/lib/boletoVivoDaFatura';
import { bloqueioCobrancaVigor } from '../../src/lib/cobrancaNoMesmoMes';
import { calcularSplitParceira } from '../../src/lib/splitParceira';
import { ressalvaValida, gravarRessalvaPagador } from '../../src/lib/ressalvaPagador';
import ConflitoPagadorAviso from './components/ConflitoPagadorAviso';
import { escolherPagador, situacaoDocumento, conflitoDePagador, pareceTitularEquatorial, type ConflitoPagador } from '../../src/lib/pagadorBoleto';
import { pagadorDoAcordo, type PagadorAcordo } from '../../supabase/functions/_shared/pagadorAcordo.ts';
import { camposEmissaoSicoob, contaDaUc, NOME_CONTA_SICOOB, type ContaSicoob } from '../../src/lib/contaSicoob';
import { exigirContaLiberada } from '../../src/lib/sicoobAccount';
import { buscarTrocasDaUc, contextoCobrancaDaFatura } from '../../src/lib/trocaDonoUc';
import { fontePagadorDaUc, pagadorComOutroNome } from '../../src/lib/pagadorUc';
import { buscarPagadorDaUc } from '../../src/services/pagadores';
import { podeEditarCadastro } from '../../src/lib/permissaoCadastro';
import AlteracoesCadastroUc from '../../src/components/AlteracoesCadastroUc';
import { atualizarUcComMotivo } from '../../src/services/cadastroUc';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { contaEquatorial } from '../../src/lib/contaEquatorial';
import { criarTravaEmissao } from '../../src/lib/travaEmissao';

const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBR = (iso?: string | null) => {
    const [a, m, d] = String(iso ?? '').slice(0, 10).split('-');
    return a && m && d ? `${d}/${m}/${a}` : '—';
};
const dataHoraBR = (iso?: string | null) => (iso
    ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '');

/** Cor do nome da conta Sicoob no resumo da emissão — a mesma da tela da UC. */
const COR_TEXTO_CONTA: Record<ContaSicoob, string> = {
    associacao: 'text-teal-600 dark:text-teal-400',
    gestao: 'text-blue-600 dark:text-blue-400',
    hsolar: 'text-amber-600 dark:text-amber-400',
};

interface BoletoEmissionModalProps {
    isOpen: boolean;
    onClose: () => void;
    fatura: Fatura | null;
    onUpdate: () => void;
    onNext?: () => void;
}

const BoletoEmissionModal: React.FC<BoletoEmissionModalProps> = ({ isOpen, onClose, fatura, onUpdate, onNext }) => {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showBackConfirm, setShowBackConfirm] = useState(false);
    const [showEmitConfirm, setShowEmitConfirm] = useState(false);
    // Motivo/justificativa de emissao fora do prazo padrao (ver src/lib/janelaFaturamento.ts).
    const [showJustificativaAtraso, setShowJustificativaAtraso] = useState(false);
    const [motivoAtraso, setMotivoAtraso] = useState('');
    const [justificativaAtraso, setJustificativaAtraso] = useState('');
    // Reemissao que nasceu numa LINHA NOVA de faturas (cancelaram e lancaram de novo
    // para a mesma UC e mes): o historico desta linha nasce vazio e sozinho nao
    // denuncia a reemissao. Sem isto, uma reemissao atrasada pediria justificativa.
    const [houveEmissaoAnteriorNaUcEMes, setHouveEmissaoAnteriorNaUcEMes] = useState(false);
    const [contaEmissao, setContaEmissao] = useState<ContaSicoob>('associacao');
    const [boletoUnico, setBoletoUnico] = useState<boolean>(false);
    // Boleto único em que o cliente já pagou a fatura da Equatorial por fora. Marcado
    // aqui (por FATURA, não por UC — no mês seguinte volta ao normal), tira a parte da
    // distribuidora do valor cobrado, do split e da fila de repasse. Existe por causa
    // do caso de 20/08/2026: um cliente da Gran pagou o código de barras da Equatorial
    // em vez do boleto unificado, e a cobrança teve de ser cancelada porque manter o
    // boleto significaria cobrar a distribuidora duas vezes.
    const [equatorialQuitada, setEquatorialQuitada] = useState<boolean>(false);
    // Emissão de teste na conta sandbox do Asaas: nada de dinheiro real, e a fatura
    // sai do radar de todo relatório. Só adm enxerga (a edge function também barra).
    const [optouPorSandbox, setOptouPorSandbox] = useState<boolean>(false);
    // Fatura já marcada como teste só emite em teste. Sem isto, reemitir um teste
    // cancelado com a caixinha desmarcada cobrava de verdade (Sicoob via n8n, ou a
    // conta de produção do Asaas). Para cobrar de verdade, tire a marca antes.
    const faturaDeTeste = fatura?.ambiente === 'sandbox';
    const emitirEmSandbox = optouPorSandbox || faturaDeTeste;
    const [emissionError, setEmissionError] = useState<string | null>(null);
    // Boleto que já existe no banco com a fatura parada nesta etapa (fatura 14192,
    // 16/10/2026). Em vez do erro sem saída, a tela mostra o boleto e leva a fatura
    // para "A enviar" — de lá ela é enviada ao cliente ou cancelada.
    const [boletoParado, setBoletoParado] = useState<BoletoLido | null>(null);
    const [pendingParcelaAlert, setPendingParcelaAlert] = useState<any | null>(null);
    const [ucId, setUcId] = useState<string | null>(null);
    const [ucRegistered, setUcRegistered] = useState<boolean>(true);
    // #6239: UC cadastrada → o pagador vem do cadastro e só o Financeiro ou adm
    // o muda. Sem a permissão, nome e CPF/CNPJ do boleto ficam travados; o endereço
    // continua editável (27/10/2026), mas a correção fica só na fatura.
    // UC sem cadastro continua editável: não há de onde puxar o pagador.
    const pagadorTravado = ucRegistered && !podeEditarCadastro(profile);
    // UC com pagador próprio: a emissão não grava nome/CPF de volta no cliente.
    const [ucTemPagador, setUcTemPagador] = useState<boolean>(false);
    const [shouldSaveUc, setShouldSaveUc] = useState<boolean>(false);
    const [showUcConfirmModal, setShowUcConfirmModal] = useState<boolean>(false);
    // Controla se o usuário já editou manualmente a data limite de pagamento.
    // Enquanto false, a data limite acompanha o vencimento (vencimento + 30).
    const [limiteEditadoManualmente, setLimiteEditadoManualmente] = useState<boolean>(false);
    // Gateway de pagamento da empresa parceira da fatura (null = Vigor/Sicoob de sempre)
    // null = fatura da própria Vigor (Sicoob/n8n); 'asaas' = parceira via Asaas;
    // undefined = ainda não resolvido ou falhou — nunca tratar como "é da Vigor".
    const [bancoGateway, setBancoGateway] = useState<string | null | undefined>(undefined);
    // Empresa parceira da fatura — usada para prever o split antes de emitir
    const [empresaParceira, setEmpresaParceira] = useState<{ nome: string } | null>(null);
    // % da Vigor na tarifa cheia, da UC (desde 26/09/2026 é por UC, não da parceira).
    const [percentualVigorUc, setPercentualVigorUc] = useState<number | null>(null);
    // De qual cadastro veio o par nome+CPF/CNPJ do pagador, para mostrar na tela.
    const [origemPagador, setOrigemPagador] = useState<string>('');
    // #6565: pagador da UC com o CPF/CNPJ do cliente e outro nome — o nome do cliente.
    const [clienteComOutroNome, setClienteComOutroNome] = useState<string | null>(null);
    // Documento já usado com outro nome. Vira confirmação, não bloqueio: o histórico
    // pode estar sujo justamente por causa do erro que se quer corrigir, e barrar de
    // vez deixaria o operador sem saída (foi o que travou a emissão da ARE em 12/08).
    // Ref, não state: `executeEmitir` é chamado logo após confirmar e leria o valor antigo.
    const [pendingConflito, setPendingConflito] = useState<ConflitoPagador | null>(null);
    const conflitoLiberadoRef = useRef(false);
    // Pessoas diferentes no mesmo CPF/CNPJ: qualquer um emite, com ressalva escrita (#6160).
    const [ressalvaPagador, setRessalvaPagador] = useState('');
    const ressalvaAceitaRef = useRef<string | null>(null);
    const faltaRessalva = !!pendingConflito?.pessoaDiferente && !ressalvaValida(ressalvaPagador);
    // Esta fatura já foi emitida com sucesso nesta abertura do modal.
    //
    // Depois do sucesso o modal NÃO desmonta: `onNext` reabre o fluxo e o `finally`
    // devolve o botão, enquanto `fatura` ainda é a cópia velha em memória (o refetch
    // é enfileirado, não aguardado). Por uns segundos a tela de emissão reaparece
    // igualzinha, com o botão pronto — o operador entende que falhou e clica de novo.
    // Foi assim que nasceram os boletos 5704/5705 da fatura 11172, e os intervalos dos
    // outros casos da base (12s, 12s, 18s, 54s) mostram o mesmo reclique.
    //
    // Ref, não state: precisa sobreviver ao re-render que o refetch provoca. Zera no
    // efeito de [isOpen, fatura?.id] — ou seja, ao abrir outra fatura ou ao reabrir
    // esta (o caso legítimo: cancelou o boleto e vai emitir de novo).
    const emitidoNestaSessaoRef = useRef(false);
    // Uma emissão por vez: marcada no clique, antes de qualquer `await`. O `loading`
    // sozinho só ligava depois de `conflitoDePagador`; em 23/10/2026 o banco passou 2
    // minutos respondendo 520-525, essa consulta pendurou ~20 s por clique, o botão
    // seguiu livre e sem "Enviando...", e a fatura 14672 ganhou 4 cobranças no Asaas.
    // A asaas-cobranca também reserva a fatura (409 no pedido repetido); esta trava é a
    // que evita o clique chegar lá. Ver src/lib/travaEmissao.ts.
    const travaEmissaoRef = useRef(criarTravaEmissao());
    // Guarda o id da fatura cujo primeiro disparo do recálculo já foi ignorado.
    // Ver o comentário no efeito de auto-recálculo, mais abaixo.
    const recalculoIniciadoRef = useRef<string | number | null>(null);

    // Calculated State
    const [calculated, setCalculated] = useState({
        consumo_total_gd_s_vigor: 0,
        valor_adc_bandeira_s_vigor: 0,
        custo_s_vigor: 0
    });

    const [edited, setEdited] = useState({
        uc_codigo: '',
        nome_cliente_boleto: '',
        cpf_cnpj_cliente_boleto: '',
        end_logradouro: '',
        end_numero: '',
        end_bairro: '',
        end_cep: '',
        end_cidade: '',
        end_uf: '',
        mes_referencia: '',
        valor_boleto_vigor: 0,
        vencimento_boleto: '',
        data_limite_pagamento_boleto: '',
        valor_consumo_nao_comp: 0,
        valor_energia_nao_isenta_boleto: 0,
        valor_restituicao_imposto_boleto: 0,
        outros_multas_boleto: 0,
        outros_multas_descricao: '',
        // New editable fields requested
        energia_injetada_gd1_kwh: 0,
        tarifa_injecao_gd1: 0,
        consumo_ativo_kwh: 0,
        tarifa_bandeira: 0,
        energia_injetada_gd2_kwh: 0,
        tarifa_injecao_gd2: 0,
        tarifa_parc_injet_s_desc_gd2: 0,
        valor_parcelamentos_boleto: 0,
        valor_juros_boleto: 0,
        qtd_injecao_scee_boleto: 0,
        qtd_adc_bandeira_boleto: 0,
        qtd_parc_injet_boleto: 0,
        parcelamento_descricao: '',
        observacoes_boleto: ''
    });

    // Reset/Initialize on Fatura Change
    useEffect(() => {
        if (isOpen && fatura) {
            // `undefined` = ainda resolvendo / falhou. Diferente de `null`, que é
            // resposta legítima ("fatura da própria Vigor"). A distinção existe porque
            // tratar falha como null fazia a fatura de parceira ser emitida pelo
            // Sicoob, na conta da Vigor — ver o guard em handleConfirm.
            setBancoGateway(undefined);
            recalculoIniciadoRef.current = null;
            resolveBancoGateway(fatura.empresa_parceira_id)
                .then(setBancoGateway)
                .catch(err => {
                    setBancoGateway(undefined);
                    addToast(err?.message || 'Não foi possível identificar o banco desta fatura.', 'error', 8000);
                });
            setEmpresaParceira(null);
            setPercentualVigorUc(null);
            if (fatura.empresa_parceira_id) {
                supabase.from('empresas_parceiras')
                    .select('nome')
                    .eq('id', fatura.empresa_parceira_id)
                    .maybeSingle()
                    .then(({ data }) => setEmpresaParceira(data as any));
            }
            calculateValues();
            const vencimentoInicial = fatura.vencimento_boleto || fatura.data_vencimento || '';
            // Data limite: usa a salva na fatura, ou o padrão vencimento + 90 dias.
            setLimiteEditadoManualmente(!!fatura.data_limite_pagamento_boleto);
            // Liberação de conflito vale para uma emissão só; reabrir o modal reavalia.
            conflitoLiberadoRef.current = false;
        ressalvaAceitaRef.current = null;
        setRessalvaPagador('');
            emitidoNestaSessaoRef.current = false;
            // Fatura nova, emissão nova: a anterior (se ainda estiver voando) deixa de ser
            // desta janela e não escreve mais nela. O aviso de erro também é da fatura
            // anterior — em 23/10/2026 o erro da 14672 apareceu na janela da seguinte.
            travaEmissaoRef.current.zerar();
            setEmissionError(null);
            // Destrava o botão junto com a marca: após "emitir e próximo" o modal segue
            // montado e o `finally` deixou `loading` ligado de propósito — sem isto a
            // fatura seguinte abria parada em "Enviando..." (solicitação 5963).
            setLoading(false);
            setPendingConflito(null);
            // Idem para o motivo de atraso: cada abertura do modal coleta o seu.
            setMotivoAtraso('');
            setJustificativaAtraso('');
            setHouveEmissaoAnteriorNaUcEMes(false);
            // Cancelar e lancar de novo pode criar uma LINHA NOVA em faturas: o
            // historico_emissoes dela nasce vazio e a emissao pareceria a primeira.
            // Olhar as outras linhas da mesma UC + mes fecha essa brecha.
            if (fatura.uc_codigo && fatura.mes_referencia) {
                // Quem decide o que conta como emissão anterior é
                // `houveEmissaoDoBoletoDoMes` (lógica pura, com teste): a query traz as
                // linhas e a `classificacao`, o filtro de AVULSO fica no lib. Boleto
                // avulso da mesma UC/mês é cobrança à parte e não pode marcar a fatura
                // real como reemissão — foi assim que a fatura 11131 (AGO/2026) saiu
                // 13 dias atrasada sem pedir motivo.
                supabase.from('faturas')
                    .select('id, classificacao')
                    .eq('uc_codigo', fatura.uc_codigo)
                    .eq('mes_referencia', fatura.mes_referencia)
                    .neq('id', fatura.id)
                    .not('emitido_em', 'is', null)
                    .then(({ data }) => setHouveEmissaoAnteriorNaUcEMes(houveEmissaoDoBoletoDoMes(data)));
            }
            // Vem da FATURA, não da UC: é a decisão daquele mês. Reabrir o modal de uma
            // fatura já marcada (reemissão, por exemplo) mantém a marcação.
            //
            // Débito automático já marca sozinho: a Equatorial debita a conta na conta
            // bancária do cliente, então cobrá-la no boleto único seria a mesma conta
            // duas vezes. É o mesmo dinheiro que a guarda de 17/08 impede de REPASSAR —
            // mas ela age depois do pagamento e não desfaz a cobrança a mais.
            // Deliberadamente vence um `false` gravado: errar para o lado de não cobrar
            // custa uma reemissão, errar para o outro custa o dobro no bolso do cliente.
            // Só a escolha do operador. Débito automático NÃO entra mais aqui: até
            // 12/09/2026 ele ligava esta caixa sozinho, e o efeito colateral era gravar
            // `equatorial_quitada_pelo_cliente = true` numa fatura em que o cliente não
            // quitou nada — a distribuidora é que debita a conta dele. São causas
            // diferentes e a coluna passava a mentir. A proteção continua: quem zera a
            // parte da Equatorial agora é `excluirEquatorial`, que soma os dois casos sem
            // confundi-los.
            setEquatorialQuitada(fatura.equatorial_quitada_pelo_cliente === true);
            setEdited({
                uc_codigo: fatura.uc_codigo || '',
                // Não usar o titular (fatura.cliente_nome) aqui: o fetch da UC abaixo preenche o
                // PAGADOR (ucData.nome_cliente); o titular fica só como fallback final (UC não achada).
                nome_cliente_boleto: fatura.nome_cliente_boleto || '',
                cpf_cnpj_cliente_boleto: fatura.cpf_cnpj_cliente_boleto || '',
                end_logradouro: fatura.end_logradouro || '',
                end_numero: fatura.end_numero || '',
                end_bairro: fatura.end_bairro || '',
                end_cep: fatura.end_cep || '',
                end_cidade: fatura.end_cidade || '',
                end_uf: fatura.end_uf || '',
                mes_referencia: fatura.mes_referencia || '',
                valor_boleto_vigor: Number(fatura.valor_boleto_vigor || 0),
                vencimento_boleto: vencimentoInicial,
                data_limite_pagamento_boleto: fatura.data_limite_pagamento_boleto || addDaysISO(vencimentoInicial, 90),
                valor_consumo_nao_comp: Number(fatura.valor_consumo_nao_comp || 0),
                valor_energia_nao_isenta_boleto: Number(fatura.valor_energia_nao_isenta_boleto || 0),
                valor_restituicao_imposto_boleto: Number(fatura.valor_restituicao_imposto_boleto || 0),
                outros_multas_boleto: Number(fatura.outros_multas_boleto || 0),
                outros_multas_descricao: fatura.outros_multas_descricao || '',
                valor_parcelamentos_boleto: Number(fatura.valor_parcelamentos_boleto || 0),
                valor_juros_boleto: Number(fatura.valor_juros_boleto || 0),
                energia_injetada_gd1_kwh: Number(fatura.energia_injetada_gd1_kwh || 0),
                tarifa_injecao_gd1: Number(fatura.valor_tarifa_distribuidora || 0) * (1 - Number(fatura.desconto_tarifa || 0)),
                consumo_ativo_kwh: Number(fatura.consumo_ativo_kwh || 0),
                tarifa_bandeira: Number(fatura.tarifa_bandeira || 0),
                energia_injetada_gd2_kwh: Number(fatura.energia_injetada_gd2_kwh || 0),
                tarifa_injecao_gd2: Number(fatura.tarifa_injecao_gd2 || 0),
                tarifa_parc_injet_s_desc_gd2: Number(fatura.tarifa_parc_injet_s_desc_gd2 || 0),
                qtd_injecao_scee_boleto: Number(fatura.qtd_injecao_scee_boleto ?? (Number(fatura.energia_injetada_gd1_kwh || 0) + Number(fatura.energia_injetada_gd2_kwh || 0))),
                qtd_adc_bandeira_boleto: Number(fatura.qtd_adc_bandeira_boleto ?? (fatura.energia_injetada_gd1_kwh || 0)),
                qtd_parc_injet_boleto: Number(fatura.qtd_parc_injet_boleto ?? (fatura.energia_injetada_gd2_kwh || 0)),
                parcelamento_descricao: fatura.parcelamento_descricao || '',
                observacoes_boleto: fatura.observacoes_boleto || ''
            });

            // --- BUSCA DADOS DA UC (conta Sicoob + parcelamento pendente) ---
            const checkPendingParcela = async () => {
                try {
                    // A resolução da UC é do BANCO (resolver_uc_por_codigo), não daqui.
                    //
                    // Antes isto era `.or(numero_uc.eq.X, outro_numero_uc.eq.X)` e depois
                    // um findBestUC que normalizava no cliente — mas normalizar a lista
                    // não ajuda quando a própria consulta já voltou vazia: parte das UCs
                    // está cadastrada formatada ('2.460.767.012-08', ou o formatado no
                    // outro_numero_uc) e a fatura chega só com dígitos. Resultado: em toda
                    // fatura da Gran aparecia "ATENÇÃO: UC Não Encontrada" oferecendo
                    // criar uma UC nova — ou seja, convidando a duplicar o cadastro.
                    //
                    // A função do banco casa exato ou normalizado, nos dois campos, e já
                    // desempata as UCs duplicadas com a mesma regra de faturas.uc_codigo_norm.
                    const { data: ucList } = await supabase
                        .rpc('resolver_uc_por_codigo', { p_codigo: fatura.uc_codigo });

                    const ucData = (Array.isArray(ucList) ? ucList[0] : ucList) || null;

                    // Cobrança de acordo em que outra pessoa assumiu a dívida (comprou o
                    // estabelecimento) com "boletos no nome do responsável": o pagador é o
                    // do ACORDO, com o endereço dele — não o assinante nem o cadastro da
                    // UC, que continuam sendo do titular. Sem isto, o boleto voltava para
                    // o titular aqui, na hora de emitir. Mesma regra da edge do Asaas
                    // (supabase/functions/_shared/pagadorAcordo.ts).
                    let pagadorAcordo: PagadorAcordo | null = null;
                    if (fatura.parcelamento_origem_id) {
                        const { data: acordo } = await supabase
                            .from('parcelamentos')
                            .select('termo_dados')
                            .eq('id', fatura.parcelamento_origem_id)
                            .maybeSingle();
                        pagadorAcordo = pagadorDoAcordo(acordo?.termo_dados);
                    }
                    const camposPagadorAcordo = pagadorAcordo ? {
                        nome_cliente_boleto: pagadorAcordo.nome,
                        cpf_cnpj_cliente_boleto: pagadorAcordo.documento,
                        end_logradouro: pagadorAcordo.endereco.logradouro,
                        end_numero: pagadorAcordo.endereco.numero,
                        end_bairro: pagadorAcordo.endereco.bairro,
                        end_cep: pagadorAcordo.endereco.cep,
                        end_cidade: pagadorAcordo.endereco.cidade,
                        end_uf: pagadorAcordo.endereco.uf,
                    } : null;

                    if (ucData) {
                        setUcId(ucData.id);
                        setUcRegistered(true);
                        setContaEmissao(contaDaUc(ucData));
                        setBoletoUnico(ucData.boleto_unico ?? false);
                        setPercentualVigorUc(ucData.percentual_vigor != null ? Number(ucData.percentual_vigor) : null);

                        // O boleto sai no nome de quem ASSINOU o termo com a Vigor (o
                        // assinante, resolvido pela negociação vinculada à UC) — nunca no
                        // titular da fatura da Equatorial, que existe no sistema só para
                        // registro. São papéis deliberadamente distintos: há assinantes
                        // ativos cuja conta de luz continua no nome de outra pessoa (caso
                        // Darcy/Bruno, UC 69401701217, ago/2026). Cadastro direto na UC é
                        // fallback para UC sem negociação vinculada. "Cálculo anterior da
                        // fatura" só entra se não for indistinguível do titular Equatorial
                        // desta mesma fatura — senão a contaminação de antes do fix
                        // atravessa disfarçada sob esse rótulo.
                        //
                        // Nome e documento saem SEMPRE da mesma fonte. Compor nome do cadastro
                        // com CNPJ de outro lugar foi o que fez os boletos da AFCOM saírem no
                        // CNPJ da ARE — ver src/lib/pagadorBoleto.ts.
                        //
                        // Troca de dono (venda do estabelecimento): com o pedido aberto a UC
                        // já está na negociação nova, mas o boleto ainda é do dono anterior —
                        // e fatura carimbada dele continua dele numa reemissão. Ver
                        // contextoCobrancaDaFatura em src/lib/trocaDonoUc.ts.
                        const trocas = await buscarTrocasDaUc(supabase, ucData.id).catch(() => []);
                        const contexto = contextoCobrancaDaFatura(ucData, fatura, trocas);
                        let negociacaoData: any = null;
                        if (contexto.negociacaoId) {
                            const { data: neg } = await supabase
                                .from('negociacoes')
                                .select('cliente_nome, cpf_cnpj')
                                .eq('id', contexto.negociacaoId)
                                .maybeSingle();
                            negociacaoData = neg;
                        }
                        const calculoAnteriorConfiavel = !pareceTitularEquatorial(
                            fatura.nome_cliente_boleto, fatura.cpf_cnpj_cliente_boleto,
                            fatura.cliente_nome, fatura.cliente_cpf_cnpj,
                        );
                        // Pagador da UC (tabela pagadores): vem antes do assinante — é o
                        // que separa quem paga do cliente (o Fundo de Morrinhos no lugar
                        // da Prefeitura). Não vale em fatura de troca de dono.
                        const pagadorDaUc = await buscarPagadorDaUc(ucData.id);
                        setUcTemPagador(!!pagadorDaUc);
                        const fonteUc = fontePagadorDaUc(pagadorDaUc, !!contexto.troca);
                        setClienteComOutroNome(fonteUc && !camposPagadorAcordo && pagadorComOutroNome(pagadorDaUc, ucData.nome_cliente, ucData.cpf_cnpj_cliente)
                            ? String(ucData.nome_cliente).trim() : null);
                        const pagador = escolherPagador([
                            ...(fonteUc ? [fonteUc] : []),
                            { origem: 'assinante (termo Vigor)', nome: negociacaoData?.cliente_nome, documento: negociacaoData?.cpf_cnpj },
                            { origem: 'cadastro da UC', nome: contexto.cadastro.nome, documento: contexto.cadastro.documento },
                            ...(calculoAnteriorConfiavel
                                ? [{ origem: 'cálculo anterior da fatura', nome: fatura.nome_cliente_boleto, documento: fatura.cpf_cnpj_cliente_boleto }]
                                : []),
                        ]);
                        setOrigemPagador(camposPagadorAcordo ? 'responsável pela dívida (acordo de parcelamento)' : pagador.origem);

                        setEdited(prev => camposPagadorAcordo ? { ...prev, ...camposPagadorAcordo } : ({
                            ...prev,
                            nome_cliente_boleto: pagador.nome,
                            cpf_cnpj_cliente_boleto: pagador.documento,
                            end_logradouro: prev.end_logradouro || ucData.logradouro || '',
                            end_numero: prev.end_numero || ucData.numero || '',
                            end_bairro: prev.end_bairro || ucData.bairro || '',
                            end_cep: prev.end_cep || ucData.cep || '',
                            end_cidade: prev.end_cidade || ucData.cidade || '',
                            end_uf: prev.end_uf || ucData.estado || ''
                        }));

                        const mesRefNormal = normalizeMesReferencia(fatura.mes_referencia);
                        const { data: pAlert } = await supabase
                            .from('parcelamento_parcelas')
                            .select('*, parcelamento:parcelamentos!inner(id, unidade_consumidora_id, numero_parcelas, descricao, termo_dados, dono_anterior_troca_id)')
                            .eq('status', 'pendente')
                            .eq('mes_referencia', mesRefNormal)
                            .eq('parcelamento.unidade_consumidora_id', ucData.id)
                            .maybeSingle();

                        // Parcela de acordo com boleto no nome do responsável é cobrada à parte —
                        // sugerir juntá-la ao boleto do titular seria cobrar a pessoa errada.
                        // Mesma coisa com o acordo do dono anterior da UC (troca de dono).
                        const acordoDoDonoAnterior = !!(pAlert as any)?.parcelamento?.dono_anterior_troca_id
                            && !fatura.dono_anterior_troca_id;
                        setPendingParcelaAlert(pAlert && !acordoDoDonoAnterior && !pagadorDoAcordo((pAlert as any).parcelamento?.termo_dados) ? pAlert : null);
                    } else {
                        setUcId(null);
                        setUcRegistered(false);
                        setUcTemPagador(false);
                        setContaEmissao('associacao');
                        setBoletoUnico(false);
                        setPercentualVigorUc(null);
                        setPendingParcelaAlert(null);
                        // UC não cadastrada: não existe vínculo com negociação/termo algum,
                        // não há como saber quem é o assinante. O titular da fatura
                        // (Equatorial) nunca é fonte automática — ver caso Darcy/Bruno acima.
                        // "Cálculo anterior" só entra com a mesma guarda contra contaminação.
                        const pagadorSemUcConfiavel = !pareceTitularEquatorial(
                            fatura.nome_cliente_boleto, fatura.cpf_cnpj_cliente_boleto,
                            fatura.cliente_nome, fatura.cliente_cpf_cnpj,
                        );
                        const pagadorSemUc = escolherPagador(
                            pagadorSemUcConfiavel
                                ? [{ origem: 'cálculo anterior da fatura', nome: fatura.nome_cliente_boleto, documento: fatura.cpf_cnpj_cliente_boleto }]
                                : []
                        );
                        setOrigemPagador(camposPagadorAcordo ? 'responsável pela dívida (acordo de parcelamento)' : pagadorSemUc.origem);
                        setEdited(prev => camposPagadorAcordo ? { ...prev, ...camposPagadorAcordo } : ({
                            ...prev,
                            nome_cliente_boleto: pagadorSemUc.nome,
                            cpf_cnpj_cliente_boleto: pagadorSemUc.documento,
                        }));
                    }
                } catch (err) {
                    console.error("Erro ao checar dados da UC na emissão:", err);
                }
            };
            checkPendingParcela();
            // -------------------------------------------------
        }
    }, [isOpen, fatura?.id]);

    // Boleto parado: a fatura chegou aqui com cobrança já registrada. Confere no banco
    // (a cópia da tela pode estar velha) e, se for o caso, troca a emissão pela saída
    // para "A enviar". Só consulta quando a tela já sabe de um boleto; o que a tela não
    // sabe, a leitura do banco no clique de "Emitir" pega.
    useEffect(() => {
        setBoletoParado(null);
        if (!isOpen || !fatura?.id || !boletoJaRegistrado(fatura)) return;
        let valendo = true;
        lerBoletoDaFatura(fatura.id)
            .then(atual => { if (valendo && boletoParadoAntesDoEnvio(atual)) setBoletoParado(atual); })
            .catch(err => console.error('Erro ao conferir o boleto da fatura:', err));
        return () => { valendo = false; };
    }, [isOpen, fatura?.id, fatura?.num_cobranca_boleto]);

    /**
     * Leva para "A enviar" a fatura que já tem boleto vivo e ficou para trás no quadro.
     * Não fala com o banco nem com o gateway: o boleto é o mesmo, só a etapa muda.
     */
    const concluirBoletoParado = async () => {
        if (!fatura || !boletoParado) return;
        const bilhete = travaEmissaoRef.current.pegar();
        if (!bilhete) return;
        setLoading(true);
        try {
            // Relê: só leva adiante se continua o mesmo boleto, ainda parado.
            const atual = await lerBoletoDaFatura(fatura.id);
            const mesmoBoleto = String(atual.num_cobranca_boleto ?? '').trim()
                === String(boletoParado.num_cobranca_boleto ?? '').trim();
            if (!boletoParadoAntesDoEnvio(atual) || !mesmoBoleto) {
                throw new Error('A fatura mudou desde que esta tela abriu (outra pessoa mexeu nela). Nada foi alterado: feche e recarregue a página.');
            }

            // Sem carimbo de emissão (a emissão não terminou na tela, casos 14454 e 14032):
            // carimba agora, e o gatilho do histórico registra o boleto. Com carimbo
            // (caso 14192), quem emitiu continua sendo quem emitiu.
            const autor = profile?.full_name || user?.email || 'Sistema';
            const carimbo = atual.emitido_em ? {} : { emitido_por_nome: autor, emitido_em: new Date().toISOString() };

            // Condicional na etapa e no boleto lidos: se alguém mexer entre a leitura e
            // a gravação, nada muda.
            const { data: movidas, error } = await supabase
                .from('faturas')
                .update({ status_fatura: ETAPA_A_ENVIAR, ...carimbo })
                .eq('id', fatura.id)
                .eq('status_fatura', atual.status_fatura as string)
                .eq('num_cobranca_boleto', atual.num_cobranca_boleto as string)
                .select('id');
            if (error) throw new Error(error.message);
            if (!movidas || movidas.length === 0) {
                throw new Error('A fatura mudou enquanto esta tela gravava. Nada foi alterado: feche e recarregue a página.');
            }

            const conferencia = conferirValorDoBoleto(atual);
            await logAction(user?.id || 'SYSTEM', 'CONCLUIR_BOLETO_PARADO', {
                fatura_id: fatura.id,
                num_cobranca_boleto: atual.num_cobranca_boleto,
                etapa_anterior: atual.status_fatura,
                carimbou_emissao: !atual.emitido_em,
                valor_boleto: conferencia.valorBoleto,
                valor_fatura: conferencia.valorFatura,
                valor_bate: conferencia.bate,
            });

            addToast(
                conferencia.bate === false
                    ? `Fatura em "A enviar". NÃO envie o boleto ${atual.num_cobranca_boleto}: o valor dele não bate com o cálculo. Cancele-o por lá e emita de novo.`
                    : `Fatura em "A enviar" com o boleto ${atual.num_cobranca_boleto}. Agora é só enviar ao cliente.`,
                conferencia.bate === false ? 'warning' : 'success',
                12000,
            );
            setBoletoParado(null);
            onUpdate();
            if (travaEmissaoRef.current.ehAtual(bilhete)) {
                if (onNext) { onNext(); } else { onClose(); }
            }
        } catch (err: any) {
            addToast(err?.message || 'Não foi possível levar a fatura para "A enviar".', 'error', 12000);
        } finally {
            if (travaEmissaoRef.current.ehAtual(bilhete)) {
                travaEmissaoRef.current.soltar(bilhete);
                setLoading(false);
            }
        }
    };

    const calculateValues = () => {
        if (!fatura) return;

        const energia_injetada_gd1 = Number(fatura.energia_injetada_gd1_kwh || 0);
        const energia_injetada_gd2 = Number(fatura.energia_injetada_gd2_kwh || 0);
        const valor_tarifa = Number(fatura.valor_tarifa_distribuidora || 0);
        const tarifa_bandeira = Number(fatura.tarifa_bandeira || 0);

        // 1. valor_consumo_total_gd_s_vigor
        const consumo_total_gd_s_vigor = (energia_injetada_gd1 + energia_injetada_gd2) * valor_tarifa;

        // 2. valor_adc_bandeira_s_vigor
        const valor_adc_bandeira_s_vigor = (energia_injetada_gd1 + energia_injetada_gd2) * tarifa_bandeira;

        // 3. custo_s_vigor (Sum)
        const custo_s_vigor = consumo_total_gd_s_vigor + valor_adc_bandeira_s_vigor;

        setCalculated({
            consumo_total_gd_s_vigor,
            valor_adc_bandeira_s_vigor,
            custo_s_vigor
        });
    };

    // Auto-recalculate valor_boleto_vigor when detailed fields change
    useEffect(() => {
        if (!isOpen || !fatura) return;

        // O primeiro disparo é o de abertura, não uma edição do operador — e nele o
        // valor que veio do banco tem que valer. Numa cobrança avulsa (parcela de
        // acordo, por exemplo) os itens de energia são todos zero e o recálculo
        // zerava um boleto de R$ 1.535,75: a tela abria em R$ 0,00 e a emissão
        // ficava barrada pela trava de valor zero. A partir da 1ª edição de item o
        // total volta a acompanhar a soma, como sempre.
        if (recalculoIniciadoRef.current !== fatura.id) {
            recalculoIniciadoRef.current = fatura.id;
            return;
        }

        const desconto_bandeira_calc = Number(fatura?.desconto_bandeira || 0);
        const row1 = edited.qtd_injecao_scee_boleto * edited.tarifa_injecao_gd1;
        const row2 = edited.qtd_adc_bandeira_boleto * edited.tarifa_bandeira * (1 - desconto_bandeira_calc);
        const row3 = -(edited.qtd_parc_injet_boleto * edited.tarifa_parc_injet_s_desc_gd2);
        const row4 = edited.valor_restituicao_imposto_boleto;
        const row5 = edited.valor_energia_nao_isenta_boleto;
        const row6 = edited.valor_juros_boleto + edited.outros_multas_boleto;
        const row7 = edited.valor_parcelamentos_boleto;

        const dynamicTotal = row1 + row2 + row3 + row4 + row5 + row6 + row7;

        setEdited(prev => {
            if (Math.abs(prev.valor_boleto_vigor - dynamicTotal) > 0.001) {
                return { ...prev, valor_boleto_vigor: dynamicTotal };
            }
            return prev;
        });

    }, [
        edited.qtd_injecao_scee_boleto,
        edited.qtd_adc_bandeira_boleto,
        edited.qtd_parc_injet_boleto,
        edited.tarifa_injecao_gd1,
        edited.consumo_ativo_kwh,
        edited.tarifa_bandeira,
        edited.tarifa_injecao_gd2,
        edited.valor_restituicao_imposto_boleto,
        edited.valor_energia_nao_isenta_boleto,
        edited.valor_juros_boleto,
        edited.outros_multas_boleto,
        edited.valor_parcelamentos_boleto,
        isOpen,
        fatura?.id
    ]);

    // Prazo do agrupamento desta UC (#5787). Usa a MESMA fonte da tela de acompanhamento —
    // feriados cadastrados, régua própria da Secretaria e datas ajustadas do mês —, senão a
    // emissão pediria justificativa por um limite diferente do que a tela mostra.
    const [limiteAgrupamentoIso, setLimiteAgrupamentoIso] = useState<string | null>(null);
    // UC cobrada por agrupamento não pode ter boleto próprio (#5785). A trava de verdade roda no
    // clique, com dado fresco; guardar o nome aqui é para AVISAR antes, em vez de deixar o
    // operador descobrir só depois de mandar emitir.
    const [agrupamentoDaUc, setAgrupamentoDaUc] = useState<string | null>(null);
    useEffect(() => {
        let cancelado = false;
        const competencia = competenciaParaData(fatura?.mes_referencia);
        if (!isOpen || !fatura?.uc_codigo || !competencia) { setLimiteAgrupamentoIso(null); setAgrupamentoDaUc(null); return; }
        (async () => {
            try {
                const prazo = await prazoAgrupamentoDaUc(fatura.uc_codigo, competencia, new Date().toISOString().slice(0, 10));
                if (cancelado) return;
                setLimiteAgrupamentoIso(prazo?.regua.limiteEmissao ?? null);
                setAgrupamentoDaUc(prazo?.agrupamentoNome ?? null);
            } catch (e) {
                if (cancelado) return;
                // Falhar aqui volta para a régua individual (lado seguro: pede justificativa a
                // mais, nunca a menos), mas o operador precisa saber por que o prazo mudou.
                console.error('Prazo do agrupamento da UC:', e);
                setLimiteAgrupamentoIso(null);
                setAgrupamentoDaUc(null);
                addToast('Não foi possível confirmar o agrupamento desta UC; o prazo individual foi usado.', 'warning');
            }
        })();
        return () => { cancelado = true; };
    }, [isOpen, fatura?.uc_codigo, fatura?.mes_referencia]);

    // Esta emissão precisa de motivo/justificativa? A regra e as exceções (avulso,
    // reemissão, agrupamento) estão em src/lib/janelaFaturamento.ts, com testes. O prazo é
    // medido contra o `data_vencimento` da EQUATORIAL — que não é editável nesta tela —,
    // então o resultado não muda enquanto o usuário mexe no formulário.
    const exigenciaJustificativa = useMemo(() => exigeJustificativaAtraso({
        dataVencimento: fatura?.data_vencimento,
        classificacao: fatura?.classificacao,
        historicoEmissoes: fatura?.historico_emissoes,
        emitidoEm: fatura?.emitido_em,
        houveEmissaoAnteriorNaUcEMes,
        limiteAgrupamentoIso,
    }), [
        fatura?.data_vencimento,
        fatura?.classificacao,
        fatura?.historico_emissoes,
        fatura?.emitido_em,
        houveEmissaoAnteriorNaUcEMes,
        limiteAgrupamentoIso,
    ]);

    // Prévia do split da empresa parceira, com os valores que estão na tela agora.
    // Só informativo — quem decide de verdade é a asaas-cobranca, que recalcula do
    // banco na hora de criar a cobrança (mesma função, src/lib/splitParceira.ts).
    const splitPreview = useMemo(() => {
        if (!fatura?.empresa_parceira_id || !empresaParceira) return null;
        return calcularSplitParceira({
            qtd_injecao_scee_boleto: edited.qtd_injecao_scee_boleto,
            tarifa_injecao_gd1: edited.tarifa_injecao_gd1,
            valor_tarifa_distribuidora: fatura.valor_tarifa_distribuidora,
            desconto_tarifa: fatura.desconto_tarifa,
            qtd_adc_bandeira_boleto: edited.qtd_adc_bandeira_boleto,
            tarifa_bandeira: edited.tarifa_bandeira,
            desconto_bandeira: fatura.desconto_bandeira,
            valor_boleto_vigor: edited.valor_boleto_vigor,
        }, percentualVigorUc);
    }, [
        fatura?.empresa_parceira_id,
        fatura?.valor_tarifa_distribuidora,
        fatura?.desconto_tarifa,
        fatura?.desconto_bandeira,
        empresaParceira,
        percentualVigorUc,
        edited.qtd_injecao_scee_boleto,
        edited.tarifa_injecao_gd1,
        edited.qtd_adc_bandeira_boleto,
        edited.tarifa_bandeira,
        edited.valor_boleto_vigor,
    ]);

    // Parte da Equatorial e o que o boleto de fato cobra. Fora do boleto único, e
    // quando o cliente já quitou a distribuidora, o boleto é só a parte da Vigor.
    //
    // O valor é o do código de barras da conta (src/lib/contaEquatorial.ts), não o
    // `valor_fatura_distribuidora` gravado: a validação gravava ali a soma das parcelas,
    // e o boleto 6938 (fatura 14432) cobrou R$ 59,49 por uma conta de R$ 46,94.
    const conta = contaEquatorial(fatura ?? {});
    const valorEquatorial = conta.valor;
    const valorGravadoEquatorial = Number(fatura?.valor_fatura_distribuidora || 0);
    // Fatura validada antes da correção: o gravado é a soma velha. A emissão corrige o
    // campo para o valor do código, e o resumo diz que corrigiu.
    const corrigeValorEquatorial = conta.fonte === 'codigo_barras'
        && Math.abs(valorEquatorial - valorGravadoEquatorial) > 0.005;
    // Vem da própria fatura da Equatorial, gravado pelo robô de extração
    // (_shared/mapeador-faturas). Explica por que a opção já veio marcada.
    const emDebitoAutomatico = fatura?.debito_automatico_equatorial === true;
    /**
     * Os dois motivos para a parte da Equatorial ficar fora do boleto, somados aqui e
     * gravados em colunas distintas: `equatorial_quitada_pelo_cliente` é escolha do
     * operador; débito automático é fato da fatura, já em `debito_automatico_equatorial`.
     *
     * Desde a decisão #5571 (Opção 2), UC em débito automático nem deveria estar no
     * boleto único — a trava está na adesão. Isto aqui é a rede embaixo: se uma escapar,
     * o boleto ainda sai sem cobrar duas vezes.
     */
    const excluirEquatorial = equatorialQuitada || emDebitoAutomatico;
    /** Por que a Equatorial ficou de fora — o resumo precisa dizer qual dos dois foi. */
    const motivoExclusaoEquatorial = equatorialQuitada
        ? 'já quitada pelo cliente'
        : emDebitoAutomatico ? 'em débito automático na Equatorial' : null;
    const equatorialNoBoleto = boletoUnico && !excluirEquatorial;
    const valorEmissaoBoletoUnico = Number(edited.valor_boleto_vigor || 0)
        + (equatorialNoBoleto ? valorEquatorial : 0);

    const handleBack = () => {
        if (!fatura) return;
        setShowBackConfirm(true);
    };

    const executeBack = async () => {
        if (!fatura) return;
        setShowBackConfirm(false);

        setLoading(true);
        try {
            await barrarSeTemBoletoVivo(fatura.id, 'retroceder');

            // O banco recusa voltar de etapa uma fatura com boleto vivo; sem checar o
            // erro, a tela registrava o retrocesso que não aconteceu.
            const { error: erroUpdate } = await supabase.from('faturas').update({
                status_fatura: 'Fatura validada'
            }).eq('id', fatura.id);
            if (erroUpdate) throw erroUpdate;

            await logAction(user?.id || 'SYSTEM', 'REVERT_INVOICE_STATUS', { fatura_id: fatura.id, new_status: 'Fatura validada' });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'reverter_status_fatura', origem: 'acao' });
            onUpdate();
            onClose();
        } catch (error: any) {
            console.error(error);
            addToast(error?.message ? `Erro ao retroceder: ${error.message}` : 'Erro ao retroceder status.', 'error', 12000);
        } finally {
            setLoading(false);
        }
    };

    const handleEmitir = async () => {
        if (!fatura) return;

        if (!ucRegistered) {
            setShowUcConfirmModal(true);
            return;
        }

        executeEmitir();
    };

    const executeEmitir = async () => {
        // Primeira barreira do reclique, antes de qualquer ida ao banco: se esta
        // abertura do modal já emitiu, o botão não emite de novo. Ela e a trava logo
        // abaixo cuidam da tela; quem garante de verdade é o banco (a leitura de
        // `num_cobranca_boleto`, os triggers e, no Asaas, a reserva da asaas-cobranca).
        // Esta aqui existe para o operador entender o que houve em vez de levar um erro.
        if (emitidoNestaSessaoRef.current) {
            addToast(
                'O boleto desta fatura JÁ foi emitido agora há pouco — a tela ainda está atualizando. '
                + 'Não clique em emitir de novo: isso criaria uma segunda cobrança. '
                + 'Feche o modal ou recarregue a página para ver o boleto.',
                'warning',
                10000,
            );
            return;
        }

        // Segunda barreira, e a que segura o clique repetido: marcada AGORA, antes de
        // qualquer `await`. Até a resposta voltar, todo clique novo para aqui.
        const bilhete = travaEmissaoRef.current.pegar();
        if (!bilhete) {
            addToast('A emissão desta fatura já está em andamento. Aguarde a resposta antes de clicar de novo.', 'info');
            return;
        }
        setLoading(true);
        setEmissionError(null);
        try {
            await realizarEmissao(bilhete);
        } finally {
            // Emissão que saiu NÃO devolve o botão nem a trava (o modal segue montado
            // com a fatura velha até o refetch — destravar convidaria o reclique). Nas
            // saídas sem emissão (validação, aviso de pagador, erro) devolve os dois.
            // Se a janela já trocou de fatura, nada aqui é mais dela.
            if (travaEmissaoRef.current.ehAtual(bilhete) && !emitidoNestaSessaoRef.current) {
                travaEmissaoRef.current.soltar(bilhete);
                setLoading(false);
            }
        }
    };

    const realizarEmissao = async (bilhete: symbol) => {
        if (!fatura) return;
        // A janela ainda é desta emissão? Depois de trocar de fatura, não é mais.
        const daJanela = () => travaEmissaoRef.current.ehAtual(bilhete);

        // Motivo do atraso é OBRIGATÓRIO e a checagem mora aqui, não só no onClick do
        // botão. O botão continua abrindo o modal na hora certa (é a boa UX), mas o
        // que garante a obrigatoriedade é esta guarda: ela alcança QUALQUER caminho
        // que chegue à emissão — retomada depois do conflito de pagador, confirmação
        // de UC não cadastrada, ou um botão novo que alguém acrescente amanhã.
        //
        // O lugar é este, e não o UPDATE de status: a cobrança nasce no gateway ANTES
        // daquele UPDATE, então recusar lá deixaria o cliente cobrado com a fatura
        // parada — foi assim que saíram as cobranças duplicadas de 24/08/2026. Aqui
        // ainda não se chamou gateway nenhum, então abortar é seguro.
        if (exigenciaJustificativa.exige && !motivoAtraso) {
            setShowEmitConfirm(false);
            setShowJustificativaAtraso(true);
            addToast('Esta emissão está fora do prazo padrão: informe o motivo antes de emitir.', 'error');
            return;
        }

        // Valida que a data limite de pagamento não é anterior ao vencimento.
        if (
            edited.vencimento_boleto &&
            edited.data_limite_pagamento_boleto &&
            edited.data_limite_pagamento_boleto < edited.vencimento_boleto
        ) {
            addToast('A data limite de pagamento não pode ser anterior ao vencimento do boleto.', 'error');
            return;
        }

        // Boleto zerado não existe. O caso real é a UC de puro repasse (injeção zero,
        // o boleto único é só a conta da Equatorial): marcada a quitação pelo cliente,
        // não sobra nada a cobrar e o certo é não emitir. Barrar aqui evita o erro
        // genérico que viria do Asaas e diz o que de fato aconteceu.
        if (valorEmissaoBoletoUnico < 0.01) {
            addToast(
                excluirEquatorial
                    ? `Nada a cobrar: a fatura da Equatorial está ${motivoExclusaoEquatorial} e a parte da Vigor é R$ 0,00. Não emita boleto para esta fatura.`
                    : 'O valor do boleto é R$ 0,00. Confira o cálculo antes de emitir.',
                'error',
                8000,
            );
            return;
        }

        // O Sicoob mantém UM cadastro de pagador por CPF/CNPJ: emitir com documento
        // errado, ou com o documento de outro cliente, reescreve o nome do pagador de
        // todos os boletos daquele documento. Foi o que aconteceu com AFCOM x ARE.
        const situacaoDoc = situacaoDocumento(edited.cpf_cnpj_cliente_boleto);
        if (situacaoDoc === 'vazio') {
            addToast(
                `Sem CPF/CNPJ do assinante para a UC ${edited.uc_codigo || fatura.uc_codigo}. O sistema não usa mais o titular da conta de luz como pagador automático — vá em Unidades, vincule esta UC à negociação/termo do assinante (ou preencha o cadastro manualmente) e volte para emitir.`,
                'error',
                8000,
            );
            return;
        }
        if (situacaoDoc === 'invalido') {
            addToast(`CPF/CNPJ do pagador é inválido: ${edited.cpf_cnpj_cliente_boleto}. Corrija antes de emitir.`, 'error');
            return;
        }

        if (!conflitoLiberadoRef.current) {
            const conflito = await conflitoDePagador(edited.cpf_cnpj_cliente_boleto, edited.nome_cliente_boleto, fatura.id);
            if (conflito) {
                setPendingConflito(conflito);
                return;
            }
        }

        try {
            // Já existe boleto vivo nesta fatura? A pergunta é respondida pelo BANCO,
            // nunca pelo `fatura` que o modal recebeu: é justamente o estado velho da
            // tela que produz o reclique (ver boletoJaRegistrado em
            // src/lib/emissaoBoletoValida.ts — fatura 11172, boletos 5704/5705).
            //
            // O lugar é aqui, antes do primeiro UPDATE e de qualquer gateway: mais
            // adiante a cobrança já nasceu e abortar deixaria o cliente cobrado.
            let boletoAtual: BoletoLido;
            try {
                boletoAtual = await lerBoletoDaFatura(fatura.id);
            } catch (erroBoletoAtual: any) {
                // Falha de leitura não libera a emissão: sem saber se há boleto vivo, o
                // risco de duplicar é maior que o de fazer o operador tentar de novo.
                throw new Error(
                    'Não foi possível conferir se esta fatura já tem boleto registrado: '
                    + (erroBoletoAtual?.message || 'erro desconhecido')
                    + '. A emissão foi interrompida por segurança e NENHUMA cobrança foi criada. '
                    + 'Recarregue a página e tente de novo.',
                );
            }

            if (boletoJaRegistrado(boletoAtual)) {
                // Boleto vivo com a fatura parada antes do envio: em vez do erro, a
                // tela mostra o boleto e a saída para "A enviar" (fatura 14192).
                if (boletoParadoAntesDoEnvio(boletoAtual)) {
                    if (daJanela()) setBoletoParado(boletoAtual);
                    return;
                }
                throw new Error(motivoBoletoJaRegistrado(boletoAtual.num_cobranca_boleto));
            }

            // Conta Sicoob ainda desligada (a HSolar nasceu assim): o n8n não conhece a
            // conta e o boleto sairia na Associação. Antes do primeiro UPDATE, como a
            // trava acima.
            if (!emitirEmSandbox && bancoGateway !== 'asaas') {
                await exigirContaLiberada(contaEmissao);
            }

            const totalComVigor = (edited.valor_boleto_vigor || 0)
                                - (edited.valor_juros_boleto || 0)
                                - (edited.outros_multas_boleto || 0)
                                - (edited.valor_parcelamentos_boleto || 0);

            const economia = calculated.custo_s_vigor - totalComVigor;

            let initialBoletoStatus = 'Em aberto';
            if (edited.vencimento_boleto) {
                const [year, month, day] = edited.vencimento_boleto.split('T')[0].split('-');
                const vencimentoLocal = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                vencimentoLocal.setHours(0, 0, 0, 0);
                const hoje = new Date();
                hoje.setHours(0, 0, 0, 0);
                if (vencimentoLocal < hoje) {
                    initialBoletoStatus = 'Vencido';
                }
            }

            // 1. Save calculations and edited fields to DB first
            await supabase.from('faturas').update({
                consumo_total_gd_s_vigor: calculated.consumo_total_gd_s_vigor,
                valor_adc_bandeira_s_vigor: calculated.valor_adc_bandeira_s_vigor,
                custo_s_vigor: calculated.custo_s_vigor,
                economia: economia,
                total_com_vigor: totalComVigor,

                // Save edited fields
                uc_codigo: edited.uc_codigo,
                nome_cliente_boleto: edited.nome_cliente_boleto,
                cpf_cnpj_cliente_boleto: edited.cpf_cnpj_cliente_boleto,
                end_logradouro: edited.end_logradouro,
                end_numero: edited.end_numero,
                end_bairro: edited.end_bairro,
                end_cep: edited.end_cep,
                end_cidade: edited.end_cidade,
                end_uf: edited.end_uf,
                mes_referencia: edited.mes_referencia,
                valor_boleto_vigor: edited.valor_boleto_vigor,
                vencimento_boleto: edited.vencimento_boleto,
                data_limite_pagamento_boleto: edited.data_limite_pagamento_boleto || null,
                valor_consumo_nao_comp: edited.valor_consumo_nao_comp,
                valor_energia_nao_isenta_boleto: edited.valor_energia_nao_isenta_boleto,
                valor_restituicao_imposto_boleto: edited.valor_restituicao_imposto_boleto,
                outros_multas_boleto: edited.outros_multas_boleto,
                outros_multas_descricao: edited.outros_multas_descricao,
                valor_parcelamentos_boleto: edited.valor_parcelamentos_boleto,
                valor_juros_boleto: edited.valor_juros_boleto,
                energia_injetada_gd1_kwh: edited.energia_injetada_gd1_kwh,
                tarifa_injecao_gd1: edited.tarifa_injecao_gd1,
                consumo_ativo_kwh: edited.consumo_ativo_kwh,
                tarifa_bandeira: edited.tarifa_bandeira,
                energia_injetada_gd2_kwh: edited.energia_injetada_gd2_kwh,
                tarifa_injecao_gd2: edited.tarifa_injecao_gd2,
                tarifa_parc_injet_s_desc_gd2: edited.tarifa_parc_injet_s_desc_gd2,
                qtd_injecao_scee_boleto: edited.qtd_injecao_scee_boleto,
                qtd_adc_bandeira_boleto: edited.qtd_adc_bandeira_boleto,
                qtd_parc_injet_boleto: edited.qtd_parc_injet_boleto,
                parcelamento_descricao: edited.parcelamento_descricao,
                observacoes_boleto: edited.observacoes_boleto,
                status_boleto_vigor: initialBoletoStatus,

                // Boleto único: valor total emitido no boleto — igual a valor_boleto_vigor
                // quando a UC não aderiu (ou quando o cliente já quitou a Equatorial por
                // fora), ou somado ao valor da Equatorial quando aderiu.
                // Deliberadamente separado de valor_boleto_vigor, que alimenta o cálculo de
                // comissão em AuditoriaPagamentosView e não pode ser inflado por isso.
                valor_emissao_boleto_unico: valorEmissaoBoletoUnico,

                // Conta da Equatorial = o código de barras dela. Gravar aqui mantém tudo o que
                // lê o campo depois da emissão de acordo com o boleto: a marcação do que o
                // boleto cobra (fn_faturas_marca_equatorial_no_boleto), a fila de pagamento,
                // o split do Asaas e o PDF.
                ...(corrigeValorEquatorial ? { valor_fatura_distribuidora: valorEquatorial } : {}),

                // Só faz sentido no boleto único; fora dele a Equatorial nunca esteve no
                // boleto e gravar `true` aqui seria ruído que confundiria o repasse.
                equatorial_quitada_pelo_cliente: boletoUnico && equatorialQuitada,

            }).eq('id', fatura.id);

            // 1.1 Sync customer data to unidades_consumidoras (requested update)
            //
            // Fatura de acordo de parcelamento (entrada ou parcela) NÃO escreve no
            // cadastro da UC. O pagador dela pode ser o responsável que assumiu a
            // dívida, e copiar esse nome/CPF/endereço trocava o cliente da UC: a fatura
            // mensal seguinte do titular sairia no nome do responsável (foi o que o
            // teste de 23/09/2026 fez numa UC real). Sem responsável, o pagador já é o
            // do cadastro e não há o que sincronizar.
            const faturaDeAcordo = !!fatura.parcelamento_origem_id;
            // Só registra se algo mudou de fato (o gatilho compara): o Faturamento vê no
            // próximo cálculo de onde veio a correção.
            const motivoEmissao = `Corrigido na emissão do boleto de ${fatura.mes_referencia || 'mês sem referência'} (fatura #${fatura.id}).`;
            try {
                if (faturaDeAcordo) {
                    // nada: ver acima
                } else if (ucRegistered && ucId && !podeEditarCadastro(profile)) {
                    // #6239: sem a permissão, a emissão não escreve no cadastro da UC
                    // (nome e CPF/CNPJ ficam travados na tela; o banco barraria). O
                    // endereço corrigido na tela vale só para a fatura, gravada acima.
                } else if (ucRegistered && ucId && ucTemPagador) {
                    // Com pagador próprio (tabela pagadores), nome e CPF/CNPJ do boleto
                    // NÃO voltam para o cliente da UC — senão emitir o boleto do Fundo
                    // trocaria o cliente "Prefeitura" pelo Fundo. Só o endereço segue.
                    await atualizarUcComMotivo(ucId, {
                        logradouro: edited.end_logradouro,
                        numero: edited.end_numero,
                        bairro: edited.end_bairro,
                        cep: edited.end_cep,
                        cidade: edited.end_cidade,
                        estado: edited.end_uf,
                        endereco: `${edited.end_logradouro}, ${edited.end_numero} - ${edited.end_bairro}, ${edited.end_cidade} - ${edited.end_uf}, CEP: ${edited.end_cep}`
                    }, motivoEmissao);
                } else if (ucRegistered && ucId) {
                    await atualizarUcComMotivo(ucId, {
                        nome_cliente: edited.nome_cliente_boleto,
                        cpf_cnpj_cliente: edited.cpf_cnpj_cliente_boleto,
                        logradouro: edited.end_logradouro,
                        numero: edited.end_numero,
                        bairro: edited.end_bairro,
                        cep: edited.end_cep,
                        cidade: edited.end_cidade,
                        estado: edited.end_uf,
                        endereco: `${edited.end_logradouro}, ${edited.end_numero} - ${edited.end_bairro}, ${edited.end_cidade} - ${edited.end_uf}, CEP: ${edited.end_cep}`
                    }, motivoEmissao);
                } else if (!ucRegistered && shouldSaveUc) {
                    const { data: newUc, error: insertError } = await supabase.from('unidades_consumidoras').insert({
                        numero_uc: edited.uc_codigo,
                        nome_cliente: edited.nome_cliente_boleto,
                        cpf_cnpj_cliente: edited.cpf_cnpj_cliente_boleto,
                        logradouro: edited.end_logradouro,
                        numero: edited.end_numero,
                        bairro: edited.end_bairro,
                        cep: edited.end_cep,
                        cidade: edited.end_cidade,
                        estado: edited.end_uf,
                        endereco: `${edited.end_logradouro}, ${edited.end_numero} - ${edited.end_bairro}, ${edited.end_cidade} - ${edited.end_uf}, CEP: ${edited.end_cep}`,
                        // Situação fica indefinida até o rateio confirmar a UC — cadastro
                        // avulso não implica que ela está de fato recebendo energia.
                        situacao: null,
                        // Usuário de empresa parceira só cadastra UC para a própria empresa
                        // (RLS já reforça isso).
                        ...(profile?.role === 'empresa_parceira_admin' || profile?.role === 'empresa_parceira_operador'
                            ? { empresa_parceira_id: profile.empresa_parceira_id }
                            : {}),
                    }).select('id').single();

                    if (!insertError && newUc) {
                        setUcId(newUc.id);
                        setUcRegistered(true);
                        // Sem este log a UC nasce anonima: foi assim que os cadastros
                        // duplicados de 2026 ficaram sem autor identificavel.
                        await logAction(user?.id || 'SYSTEM', 'Criou Unidade Consumidora', {
                            uc_id: newUc.id,
                            uc_number: edited.uc_codigo,
                            cliente: edited.nome_cliente_boleto,
                            origem: 'BoletoEmissionModal',
                        });
                        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'criar_uc_emissao_boleto', origem: 'acao' });
                    }
                }
            } catch (syncError) {
                console.error("Erro ao sincronizar dados do cliente para unidades_consumidoras:", syncError);
                // Emission must proceed even on failure here.
            }


            // UC de agrupamento ativo (#5785) é cobrada pelo boleto do agrupamento, nunca por
            // boleto próprio: emitir aqui cobraria a Prefeitura duas vezes. Fica na tela, antes de
            // falar com o banco, porque o registro no Sicoob só volta ao banco depois — uma trava
            // no banco deixaria o título criado e solto em vez de impedir.
            {
                const codigoUc = String(edited.uc_codigo || fatura.uc_codigo || '').replace(/\D/g, '');
                const variantes = [...new Set([codigoUc, codigoUc.replace(/^0+/, '')].filter(Boolean))];
                if (variantes.length > 0) {
                    const { data: noGrupo, error: erroGrupo } = await supabase
                        .from('vw_agrupamento_faturamento_uc')
                        .select('agrupamento_nome, agrupamento_ativo')
                        .in('numero_uc', variantes)
                        .eq('agrupamento_ativo', true)
                        .limit(1);
                    if (erroGrupo) {
                        throw new Error('Não foi possível confirmar se esta UC é cobrada por um agrupamento. Tente de novo antes de emitir.');
                    }
                    if (noGrupo && noGrupo.length > 0) {
                        throw new Error(
                            `Esta UC é cobrada pelo boleto do agrupamento "${noGrupo[0].agrupamento_nome}". `
                            + 'Deixe a fatura calculada e emita pelo Acompanhamento mensal do agrupamento. '
                            + 'Para cobrar esta UC à parte, retire-a do agrupamento antes.',
                        );
                    }
                }
            }

            // UC de cliente próprio da Gran, ou avulso da Gran vivo no mesmo mês: a
            // cobrança dessa energia é da Gran (chamado #6000). Mesmo lugar e mesmo motivo
            // da trava de agrupamento acima: antes do banco.
            if (!fatura.fora_parceria_vigor) {
                const bloqueio = await bloqueioCobrancaVigor(supabase, edited.uc_codigo || fatura.uc_codigo, fatura.mes_referencia);
                if (bloqueio) throw new Error(bloqueio);
            }

            // Trava de rota: fatura de empresa parceira NUNCA pode ser emitida pelo
            // caminho da Vigor. Sem isto, qualquer falha em resolver o banco (RLS de
            // empresas_parceiras bloqueia quem não é adm, ou a página ficou com o
            // vínculo antigo em memória) fazia a cobrança sair no Sicoob, na conta da
            // Vigor — foi o que gerou os boletos 4601 e 4603 em 10/08/2026.
            if (fatura.empresa_parceira_id && bancoGateway !== 'asaas') {
                throw new Error(
                    bancoGateway === undefined
                        ? 'Não foi possível confirmar o banco da empresa parceira desta fatura. Recarregue a página e tente novamente — emitir agora cobraria na conta da Vigor.'
                        : `Esta fatura é de empresa parceira, cujo gateway é "${bancoGateway}". A emissão por aqui cobraria na conta da Vigor e foi bloqueada.`,
                );
            }

            if (emitirEmSandbox && bancoGateway !== 'asaas') {
                // Fatura da própria Vigor (caminho Sicoob/n8n): não existe emissão de
                // teste no banco — o sandbox do Sicoob só devolve mocks. A function
                // preenche a fatura com dados fictícios e a marca como sandbox.
                await emitirBoletoTeste(fatura.id);
            } else if (bancoGateway === 'asaas') {
                // Empresa parceira com gateway Asaas — cria a cobrança direto via edge function,
                // sem passar pelo webhook n8n/Sicoob usado pela Vigor.
                await criarCobrancaAsaas(fatura.id, 'BOLETO', emitirEmSandbox ? 'sandbox' : 'producao');

                // Boleto único (Gran): monta o PDF combinado (layout da Gran + fatura da
                // Equatorial com marca d'água) a partir dos dados que a cobrança acabou de
                // gerar. Falha aqui não deve reverter a cobrança já criada no Asaas — só avisa,
                // deixando o retry para depois (a fatura original da Equatorial pode ainda não
                // ter chegado, por exemplo).
                if (boletoUnico) {
                    try {
                        await gerarBoletoGran(fatura.id);
                    } catch (pdfErr: any) {
                        // gerarBoletoGran já repetiu as falhas passageiras. O envio fica
                        // travado até o PDF existir (faltaPdfUnificado), então o recado é o
                        // que fazer, não só o erro.
                        addToast('Cobrança criada no Asaas, mas o PDF unificado não foi gerado: ' + (pdfErr.message || 'erro desconhecido') + ' Abra o card e clique em "Gerar PDF unificado" antes de enviar.', 'error', 12000);
                    }
                }
            } else {
                // 2. Fetch unified Webhook URL
                const { data: settings } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'boleto_emission_webhook_url')
                    .single();

                const webhookUrl = settings?.value;

                if (!webhookUrl) {
                    addToast('URL do Webhook de Emissão não configurada. Verifique as configurações de API.', 'error');
                    return;
                }

                // 3. Trigger Webhook — leva a conta Sicoob da UC para o n8n rotear (ver camposEmissaoSicoob)
                const { data: freshFatura } = await supabase
                    .from('faturas')
                    .select('*')
                    .eq('id', fatura.id)
                    .single();

                // valor_emissao_boleto_unico é o valor que o n8n deve efetivamente registrar
                // no Sicoob quando boleto_unico=true (Vigor + Equatorial combinados) —
                // valor_boleto_vigor continua vindo à parte, intocado, para não afetar comissão.
                const payload = {
                    ...(freshFatura || fatura),
                    ...camposEmissaoSicoob(contaEmissao),
                    boleto_unico: boletoUnico,
                    // Vai junto para o n8n saber que a parte da Equatorial não está no
                    // boleto — hoje nenhuma UC da Vigor usa boleto único, mas o payload
                    // não pode mentir se alguma passar a usar.
                    equatorial_quitada_pelo_cliente: boletoUnico && equatorialQuitada,
                    valor_emissao_boleto_unico: valorEmissaoBoletoUnico,
                };

                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    let errorMessage = `Webhook falhou (Status: ${response.status})`;
                    try {
                        const errorResponse = await response.json();
                        errorMessage = errorResponse?.mensagem || errorResponse?.message || errorResponse?.error?.message || errorMessage;
                    } catch (e) {
                        try {
                            const errorText = await response.text();
                            if (errorText) errorMessage = errorText;
                        } catch (e2) { }
                    }
                    throw new Error(errorMessage);
                }
            }

            // A chamada pode retornar status 200, mas com erros silenciosos no gateway que foram ignorados
            const { data: updatedFatura } = await supabase
                .from('faturas')
                .select('num_cobranca_boleto, url_boleto, codigo_pix_boleto, linha_digitavel_boleto')
                .eq('id', fatura.id)
                .single();

            // A regra de "esta cobrança existe mesmo?" mora em src/lib/emissaoBoletoValida.ts,
            // módulo sem I/O com teste próprio, compartilhado com o boleto avulso e com o
            // cancelamento — e espelhado pelo trigger fn_checar_integridade_boleto, que é
            // a barreira que também alcança o que o n8n grava direto na tabela.
            //
            // O teste simulado passa direto: quem grava é a edge function, que já falhou
            // alto se deu errado — não há banco para provar.
            const emissao = emissaoBoletoValida(
                { ...updatedFatura, empresa_parceira_id: bancoGateway === 'asaas' ? (fatura.empresa_parceira_id ?? 'asaas') : null },
                { sandbox: emitirEmSandbox && bancoGateway !== 'asaas' },
            );

            if (!emissao.ok) {
                throw new Error(emissao.motivo);
            }

            // 4. Update Status (SÓ AVANÇA SE TUDO DEU CERTO)
            const updatePayload: any = {
                status_fatura: 'Com boleto emitido / A enviar'
            };

            // O carimbo descreve o boleto que está VIVO agora — não a primeira tentativa.
            // Até 19/08/2026 só gravava com `if (!fatura.emitido_por_nome)`, "preservando a
            // atribuição da primeira emissão": cancelar e reemitir troca a cobrança no
            // Sicoob/Asaas mas deixava nome e data velhos na tela. A UC 344581401232 foi
            // reemitida às 11:34 de 19/08 e o modal mostrava "Alice Karoline — 12/08 09:12".
            // Quem emitiu antes continua no histórico (system_logs EMIT_BOLETO +
            // historico_cancelamentos), então nada de auditoria se perde ao sobrescrever.
            updatePayload.emitido_por_nome = profile?.full_name || user?.email || 'Sistema';
            updatePayload.emitido_em = new Date().toISOString();

            // Vai no MESMO update que `emitido_em` porque é o gatilho
            // trg_faturas_historico_emissoes que copia os três para dentro da entrada
            // de `historico_emissoes` — em updates separados o motivo ficaria de fora
            // do histórico e uma reemissão futura apagaria o da primeira emissão.
            // Sem `&& motivoAtraso`: a guarda no topo de `executeEmitir` já garante que
            // ele existe. Condicionar a gravação ao estado deixava a emissão seguir em
            // silêncio caso ele faltasse — o boleto saía e a fatura ficava sem motivo,
            // indistinguível de uma emissão no prazo.
            if (exigenciaJustificativa.exige) {
                updatePayload.motivo_atraso_emissao = motivoAtraso;
                updatePayload.justificativa_atraso_emissao = justificativaAtraso.trim() || null;
            }

            // O erro deste UPDATE PRECISA ser checado. O supabase-js nao lanca: devolve
            // { data, error }. Enquanto o retorno era ignorado, uma falha aqui passava
            // batida e o codigo seguia direto para o toast de SUCESSO — com a cobranca ja
            // criada no gateway. O operador via 'deu certo', o card nao saia de 'Com boleto
            // calculado', e reemitia. Foi assim que a trava trg_checar_integridade_boleto
            // (que ate 24/08/2026 barrava toda fatura de parceira, onde valor_boleto_vigor=0
            // e o valor real esta em valor_emissao_boleto_unico) virou 25 cobrancas
            // duplicadas na conta da Gran, R$ 27.982,69.
            //
            // A mensagem carrega o id da cobranca e manda NAO reemitir: neste ponto o
            // dinheiro ja foi cobrado do cliente, e tentar de novo so duplica.
            const { error: statusError } = await supabase.from('faturas').update(updatePayload).eq('id', fatura.id);
            if (statusError) {
                throw new Error(
                    `A cobranca FOI criada no gateway${updatedFatura?.num_cobranca_boleto ? ` (${updatedFatura.num_cobranca_boleto})` : ''}, `
                    + `mas a fatura nao pode avancar de status: ${statusError.message}. `
                    + 'NAO emita de novo — isso criaria uma segunda cobranca para o mesmo cliente. '
                    + 'Chame a Tecnologia com o numero da fatura.',
                );
            }

            // A partir daqui a cobrança existe no gateway: qualquer novo clique nesta
            // mesma tela duplicaria. Marcado ANTES dos caminhos de PDF, que têm saída
            // própria e já avisam "NÃO emita de novo". Só se a janela ainda for desta
            // fatura: marcar a seguinte como "já emitida" travaria a emissão dela.
            if (daJanela()) emitidoNestaSessaoRef.current = true;

            await logAction(user?.id || 'SYSTEM', 'EMIT_BOLETO', { fatura_id: fatura.id, ressalva_pagador: ressalvaAceitaRef.current });
            await gravarRessalvaPagador(fatura.id, ressalvaAceitaRef.current);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'emitir_boleto', origem: 'acao' });

            // Cobrança registrada, PDF ausente. O fluxo n8n desenha o PDF DEPOIS de
            // registrar o boleto no Sicoob, e o nó que chama a edge function morre em
            // qualquer 502 — quando isso acontece nada mais roda, `url_boleto` fica nula
            // e o cliente está cobrado sem que a tela tenha o que enviar. A checagem de
            // `emissaoOk` acima deixa passar de propósito (o que prova a cobrança é o
            // nosso número + a linha digitável, não a URL), então o conserto é aqui:
            // republica o PDF a partir dos dados que já estão na fatura, sem tocar na
            // cobrança. Falhou de novo? Avisa — nunca o toast de sucesso puro, que foi o
            // que fez o operador achar que estava tudo certo (chamado de 31/08/2026).
            const precisaPublicarPdf = !updatedFatura?.url_boleto
                && !(emitirEmSandbox && bancoGateway !== 'asaas')
                && bancoGateway !== 'asaas';

            if (precisaPublicarPdf) {
                try {
                    await publicarPdfBoletoVigor(fatura.id);
                    addToast('Boleto emitido. O PDF falhou na primeira tentativa e foi gerado aqui.', 'success', 8000);
                } catch (pdfErr: any) {
                    addToast(
                        `Boleto REGISTRADO no banco${updatedFatura?.num_cobranca_boleto ? ` (cobrança ${updatedFatura.num_cobranca_boleto})` : ''}, `
                        + 'mas o PDF não pôde ser gerado: ' + (pdfErr.message || 'erro desconhecido') + '. '
                        + 'NÃO emita de novo — isso criaria uma segunda cobrança. Abra a fatura e use "Gerar PDF do boleto".',
                        'error',
                        14000,
                    );
                }
                onUpdate();
                // Fechar/avançar só se a janela ainda for desta fatura: senão fecharia a
                // fatura que o operador abriu depois.
                if (daJanela()) { if (onNext) { onNext(); } else { onClose(); } }
                return;
            }

            addToast('Solicitação de emissão enviada com sucesso!', 'success');
            onUpdate();
            if (daJanela()) {
                if (onNext) {
                    onNext();
                } else {
                    onClose();
                }
            }

        } catch (error: any) {
            console.error('Error emitting boleto:', error);
            const mensagem = error.message || 'Erro desconhecido na comunicação com N8N ou Sicoob.';
            if (daJanela()) {
                setEmissionError(mensagem);
                setShowEmitConfirm(false); // Fecha o modal de confirmação para mostrar o erro na tela principal
            } else {
                // A janela já mostra outra fatura: o erro vai num aviso que diz de qual
                // fatura ele é, em vez de aparecer como se fosse da fatura aberta.
                addToast(`Fatura da UC ${fatura.uc_codigo} (${fatura.mes_referencia}): ${mensagem}`, 'error', 15000);
            }
        }
        // O botão e a trava são devolvidos (ou não) no `finally` de executeEmitir.
    };

    const handleChange = (field: string, value: string | number) => {
        if (field === 'data_limite_pagamento_boleto') {
            // Usuário definiu manualmente a data limite: para de acompanhar o vencimento.
            setLimiteEditadoManualmente(true);
            setEdited(prev => ({ ...prev, data_limite_pagamento_boleto: value as string }));
            return;
        }
        if (field === 'vencimento_boleto') {
            const novoVenc = value as string;
            setEdited(prev => ({
                ...prev,
                vencimento_boleto: novoVenc,
                // Enquanto não editada manualmente, a data limite acompanha vencimento + 90.
                data_limite_pagamento_boleto: limiteEditadoManualmente
                    ? prev.data_limite_pagamento_boleto
                    : addDaysISO(novoVenc, 90),
            }));
            return;
        }
        setEdited(prev => ({ ...prev, [field]: value }));
    };

    if (!isOpen || !fatura) return null;

    const conferenciaParado = boletoParado ? conferirValorDoBoleto(boletoParado) : null;

    const confirmBackModal = (
        <ConfirmationModal
            isOpen={showBackConfirm}
            onClose={() => setShowBackConfirm(false)}
            onConfirm={executeBack}
            title="Retroceder Etapa?"
            description='Tem certeza que deseja retroceder para "Fatura validada"?'
            confirmText="Sim, retroceder"
            cancelText="Cancelar"
            variant="warning"
        />
    );

    // Helper functions for formatting numbers in grid
    const formatBRL = (val: number) => val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatNumber = (val: number) => val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const linha1_qtd = edited.qtd_injecao_scee_boleto;
    const linha1_tarifa = edited.tarifa_injecao_gd1;

    const desconto_bandeira_calc = Number(fatura?.desconto_bandeira || 0);
    const linha2_qtd = edited.qtd_adc_bandeira_boleto;
    const linha2_tarifa = edited.tarifa_bandeira * (1 - desconto_bandeira_calc);

    const linha5_qtd = edited.qtd_parc_injet_boleto;
    const linha5_tarifa = edited.tarifa_parc_injet_s_desc_gd2;

    const linha6_outros = edited.valor_juros_boleto + edited.outros_multas_boleto;

    return (
        <>
            {confirmBackModal}
            {createPortal(
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-2 md:p-4"
                    style={{ zIndex: 9999 }}
                    {...propsFundoModal(() => onClose())}
                >
                    <div
                        className="bg-white dark:bg-gray-800/95 rounded-2xl shadow-2xl w-full max-w-[1400px] h-[95vh] overflow-hidden flex flex-col border dark:border-gray-700"
                        onClick={e => e.stopPropagation()}
                    >

                        {/* Header */}
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/40 shrink-0">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                                    <Ticket className="text-teal-500" />
                                    Confirme o boleto
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-9">
                                    {fatura.cliente_nome} • {fatura.uc_codigo} • {fatura.mes_referencia}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {fatura.pdf_fatura && (
                                    <button
                                        onClick={() => window.open(fatura.pdf_fatura, '_blank')}
                                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-secondary hover:bg-secondary/80 rounded-lg transition-colors mr-2"
                                    >
                                        <Eye size={16} />
                                        Ver PDF da fatura
                                    </button>
                                )}
                                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                                    <X size={24} className="text-gray-400" />
                                </button>
                            </div>
                        </div>

                        {emissionError && (
                            <div className="mx-6 mt-6 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 p-4 rounded-r-lg shadow-sm">
                                <div className="flex gap-3">
                                    <AlertTriangle className="text-red-500 shrink-0" size={20} />
                                    <div>
                                        <h3 className="font-bold text-red-800 dark:text-red-400 text-sm">Atenção: A emissão falhou!</h3>
                                        <p className="text-red-700 dark:text-red-300 text-sm mt-1">{emissionError}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {boletoParado && conferenciaParado && (
                            <div className="mx-6 mt-6 bg-sky-50 dark:bg-sky-900/10 border-l-4 border-sky-500 p-4 rounded-r-lg shadow-sm">
                                <div className="flex gap-3">
                                    <Info className="text-sky-600 dark:text-sky-400 shrink-0" size={20} />
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-sky-900 dark:text-sky-300 text-sm">
                                            Este boleto já foi emitido. A fatura só ficou parada nesta etapa.
                                        </h3>
                                        <p className="text-sky-800 dark:text-sky-200 text-sm mt-1">
                                            Cobrança <strong>{String(boletoParado.num_cobranca_boleto).trim()}</strong> registrada no banco
                                            {boletoParado.emitido_por_nome && (
                                                <> por <strong>{boletoParado.emitido_por_nome}</strong>{boletoParado.emitido_em ? ` em ${dataHoraBR(boletoParado.emitido_em)}` : ''}</>
                                            )}
                                            {conferenciaParado.valorBoleto !== null && (
                                                <>, no valor de <strong>{reais(conferenciaParado.valorBoleto)}</strong></>
                                            )}
                                            , com vencimento em <strong>{dataBR(boletoParado.vencimento_boleto)}</strong>.
                                            {' '}Não emita de novo: esse boleto continua valendo e o cliente pode pagá-lo.
                                            {' '}Leve a fatura para "A enviar" e mande o boleto ao cliente por lá.
                                        </p>
                                        {conferenciaParado.bate === false && conferenciaParado.valorBoleto !== null && conferenciaParado.valorFatura !== null && (
                                            <p className="text-red-700 dark:text-red-300 text-sm font-semibold mt-2">
                                                Atenção: o boleto foi registrado com {reais(conferenciaParado.valorBoleto)}, mas o cálculo atual desta fatura
                                                dá {reais(conferenciaParado.valorFatura)}, porque ela foi recalculada depois da emissão. Não envie este boleto:
                                                leve a fatura para "A enviar", cancele o boleto por lá e emita de novo.
                                            </p>
                                        )}
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {boletoParado.url_boleto && (
                                                <a
                                                    href={boletoParado.url_boleto}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-sky-700 dark:text-sky-300 border border-sky-300 dark:border-sky-700 rounded-lg hover:bg-sky-100 dark:hover:bg-sky-900/30 transition-colors"
                                                >
                                                    <Eye size={16} /> Ver boleto
                                                </a>
                                            )}
                                            <button
                                                onClick={concluirBoletoParado}
                                                disabled={loading}
                                                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-sky-600 hover:bg-sky-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <CheckCircle size={16} /> {loading ? 'Levando...' : 'Levar para "A enviar"'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Content Scroll Area */}
                        <div className="p-6 md:p-8 flex-1 overflow-y-auto">
                            <LembretesViewer uc_codigo={fatura.uc_codigo} mes_referencia={fatura.mes_referencia} />

                            {pendingParcelaAlert && (
                                <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-2xl flex gap-3.5 items-start animate-fade-in shadow-sm">
                                    <div className="p-2.5 bg-amber-500/10 rounded-xl text-amber-600 dark:text-amber-400 mt-0.5 shrink-0">
                                        <AlertTriangle size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">Atenção: Existe um parcelamento "Pendente" no cronograma!</h4>
                                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 font-medium leading-relaxed">
                                            Encontramos a parcela <strong>{pendingParcelaAlert.numero_parcela}/{pendingParcelaAlert.parcelamento?.numero_parcelas || '?'}</strong> no valor de <strong>R$ {Number(pendingParcelaAlert.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> agendada para esta UC nesta referência. Esta parcela ainda não foi provisionada ou adicionada a este boleto.
                                        </p>
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 italic font-semibold flex items-center gap-1">
                                            <Info size={12} /> Recomendação: Reconsidere retornar para a etapa "Cálculo do Boleto" para sincronizar o acréscimo.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                                {/* Left Column: Values Highlights (Vigor vs Sem Vigor) */}
                                <div className="lg:col-span-4 flex flex-col gap-6">

                                    <div className="bg-green-50 dark:bg-green-900/10 p-6 rounded-2xl border border-green-100 dark:border-green-800">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg text-green-600 dark:text-green-300">
                                                <Banknote size={24} />
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Valor do Boleto Vigor</h3>
                                        </div>
                                        <div className="text-4xl font-black text-blue-600 dark:text-blue-400 tracking-tight">
                                            R$ {formatBRL(edited.valor_boleto_vigor)}
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                            Valor final calculado na etapa anterior.
                                        </p>
                                    </div>

                                    <div className="bg-orange-50 dark:bg-white/5 p-6 rounded-2xl border border-orange-100 dark:border-orange-700">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2 bg-orange-100 dark:bg-orange-800 rounded-lg text-orange-600 dark:text-orange-300">
                                                <Calculator size={24} />
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Sem a Vigor seria:</h3>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500 dark:text-gray-400">Consumo Total GD</span>
                                                <span className="font-medium text-gray-900 dark:text-white">R$ {formatBRL(calculated.consumo_total_gd_s_vigor)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500 dark:text-gray-400">Adc. Bandeira</span>
                                                <span className="font-medium text-gray-900 dark:text-white">R$ {formatBRL(calculated.valor_adc_bandeira_s_vigor)}</span>
                                            </div>
                                            <div className="h-px bg-gray-200 dark:bg-gray-600 my-2"></div>
                                            <div className="flex justify-between items-end">
                                                <span className="font-bold text-gray-700 dark:text-gray-300">Custo Total</span>
                                                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    R$ {formatBRL(calculated.custo_s_vigor)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-green-50 dark:bg-green-900/10 p-4 rounded-xl border border-green-100 dark:border-green-800 flex items-center justify-center gap-2 mt-auto">
                                        <Info size={18} className="text-green-600 dark:text-green-400" />
                                        <p className="text-sm font-medium text-green-700 dark:text-green-300">
                                            Economia gerada: R$ {formatBRL(calculated.custo_s_vigor - edited.valor_boleto_vigor)}
                                        </p>
                                    </div>

                                </div>

                                {/* Right Column: Editable Boleto Panel */}
                                <div className="lg:col-span-8 bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-inner">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                                            <FileText size={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Informações para Boleto</h3>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Edite os campos abaixo caso seja necessário corrigir algo antes da emissão.</p>
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        {/* DADOS DO CLIENTE */}
                                        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700">
                                            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4 tracking-wide uppercase">Dados do Cliente</h4>

                                            {/* Preview não-bloqueante: o boleto sai no nome do assinante do
                                                termo Vigor, nunca no titular da conta de luz na Equatorial —
                                                ver src/lib/pagadorBoleto.ts. */}
                                            {edited.nome_cliente_boleto && origemPagador ? (
                                                <div className="mb-4 p-3.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/50 rounded-xl flex gap-3 items-start">
                                                    <CheckCircle size={18} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                                                    <p className="text-xs text-emerald-800 dark:text-emerald-300 font-medium leading-relaxed">
                                                        Este boleto vai ser cobrado de <strong>{edited.nome_cliente_boleto}</strong> — {edited.cpf_cnpj_cliente_boleto} (fonte: {origemPagador}). Confira se é mesmo o assinante antes de emitir.
                                                    </p>
                                                </div>
                                            ) : null}

                                            {clienteComOutroNome ? (
                                                <div role="alert" className="mb-4 p-3.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex gap-3 items-start">
                                                    <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                                                    <p className="text-xs text-amber-800 dark:text-amber-300 font-medium leading-relaxed">
                                                        O pagador tem o CPF/CNPJ do cliente, mas outro nome: o cliente da UC é <strong>{clienteComOutroNome}</strong>. Se o nome do boleto estiver errado, peça ao Financeiro para atualizar o pagador antes de emitir.
                                                    </p>
                                                </div>
                                            ) : null}

                                            {/* O que o Financeiro mudou no cadastro desde o último boleto
                                                (pagador, titular, cliente, desconto), com quem, quando e o motivo. */}
                                            <AlteracoesCadastroUc ucId={ucId} faturaId={fatura?.id} ucCodigo={fatura?.uc_codigo} className="mb-4" />

                                            {pagadorTravado ? (
                                                <p className="mb-4 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3.5 py-2.5">
                                                    Nome e CPF/CNPJ vêm do cadastro da UC e só o Financeiro altera. Se algum estiver errado, peça a correção antes de emitir. O endereço pode ser corrigido aqui.
                                                </p>
                                            ) : null}

                                            <fieldset disabled={pagadorTravado} className="min-w-0 disabled:opacity-70">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">UC Código</label>
                                                    <input type="text" value={edited.uc_codigo} onChange={e => handleChange('uc_codigo', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Nome do Cliente</label>
                                                    <input type="text" value={edited.nome_cliente_boleto} onChange={e => handleChange('nome_cliente_boleto', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">CPF/CNPJ</label>
                                                    <input type="text" value={edited.cpf_cnpj_cliente_boleto} onChange={e => handleChange('cpf_cnpj_cliente_boleto', e.target.value)} className={`w-full text-sm bg-gray-50 border rounded-lg px-3 py-2 dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 ${situacaoDocumento(edited.cpf_cnpj_cliente_boleto) === 'invalido' || situacaoDocumento(edited.cpf_cnpj_cliente_boleto) === 'vazio' ? 'border-red-400 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'}`} />
                                                    {situacaoDocumento(edited.cpf_cnpj_cliente_boleto) === 'vazio' ? (
                                                        <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">Sem CPF/CNPJ — a emissão vai barrar. Preencha no cadastro da UC.</p>
                                                    ) : situacaoDocumento(edited.cpf_cnpj_cliente_boleto) === 'invalido' ? (
                                                        <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">CPF/CNPJ inválido (dígito verificador não confere).</p>
                                                    ) : origemPagador ? (
                                                        <p className="mt-1 text-[11px] text-gray-400">Nome e documento vieram do {origemPagador}.</p>
                                                    ) : null}
                                                </div>
                                            </div>
                                            </fieldset>
                                            {/* O endereço fica fora da trava do #6239: o Faturamento corrige o
                                                que sai no boleto (ex.: robô que repete "S/N" no logradouro e no
                                                número). Sem a permissão, a correção vale só para esta fatura —
                                                a emissão não grava no cadastro da UC (ver "Sync customer data"). */}
                                            <h5 className="text-xs font-bold text-gray-500 mb-2">Endereço</h5>
                                            {pagadorTravado ? (
                                                <p className="mb-2 text-[11px] text-gray-400">A correção vale para este boleto. O cadastro da UC não muda.</p>
                                            ) : null}
                                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                                <div className="md:col-span-8">
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Logradouro</label>
                                                    <input type="text" value={edited.end_logradouro} onChange={e => handleChange('end_logradouro', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                </div>
                                                <div className="md:col-span-4">
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Número / Comp.</label>
                                                    <input type="text" value={edited.end_numero} onChange={e => handleChange('end_numero', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                </div>
                                                <div className="md:col-span-4">
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Bairro</label>
                                                    <input type="text" value={edited.end_bairro} onChange={e => handleChange('end_bairro', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                </div>
                                                <div className="md:col-span-3">
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">CEP</label>
                                                    <input type="text" value={edited.end_cep} onChange={e => handleChange('end_cep', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                </div>
                                                <div className="md:col-span-3">
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Cidade</label>
                                                    <input type="text" value={edited.end_cidade} onChange={e => handleChange('end_cidade', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">UF</label>
                                                    <input type="text" value={edited.end_uf} onChange={e => handleChange('end_uf', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* DADOS DE PAGAMENTO */}
                                        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 flex flex-wrap gap-4 items-center">
                                            <div className="flex-1">
                                                <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Mês Ref.</label>
                                                <input type="text" value={edited.mes_referencia} onChange={e => handleChange('mes_referencia', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Valor do Boleto Vigor</label>
                                                <input
                                                    type="text"
                                                    value={Number(edited.valor_boleto_vigor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    onChange={e => {
                                                        const digits = e.target.value.replace(/\D/g, '');
                                                        const value = parseFloat(digits) / 100;
                                                        handleChange('valor_boleto_vigor', isNaN(value) ? 0 : value);
                                                    }}
                                                    className="w-full text-sm bg-blue-50/50 border border-blue-200 rounded-lg px-3 py-2 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300 font-bold focus:ring-2 focus:ring-blue-500/50"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Vencimento Boleto</label>
                                                <input type="date" value={edited.vencimento_boleto} onChange={e => handleChange('vencimento_boleto', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Data Limite de Pagamento</label>
                                                <input type="date" min={edited.vencimento_boleto || undefined} value={edited.data_limite_pagamento_boleto} onChange={e => handleChange('data_limite_pagamento_boleto', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50" />
                                                <p className="mt-1 text-[11px] leading-tight text-gray-400 dark:text-gray-500">Até quando o boleto pode ser pago (multa/juros após o vencimento). Padrão: vencimento + 90 dias.</p>
                                            </div>
                                        </div>

                                        {/* DETALHES DA COBRANÇA */}
                                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-[#485B33] dark:border-[#5C7441] overflow-hidden shadow-sm">
                                            <div className="bg-[#485B33] px-4 py-2">
                                                <h4 className="text-white font-bold text-sm tracking-widest">DETALHES DA COBRANÇA - VIGOR ENERGY</h4>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm text-left whitespace-nowrap">
                                                    <thead className="text-xs text-[#485B33] bg-[#E8EAE6] dark:bg-[#202717] dark:text-[#A7B994]">
                                                        <tr>
                                                            <th className="px-4 py-2 font-bold w-1/2">DESCRIÇÃO</th>
                                                            <th className="px-4 py-2 font-bold text-right">QUANTIDADE</th>
                                                            <th className="px-4 py-2 font-bold text-right">TARIFA</th>
                                                            <th className="px-4 py-2 font-bold text-right">VALOR (R$)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-300">

                                                        {/* LINHA 1 */}
                                                        <tr className="bg-[#F8F9F7] dark:bg-gray-900/50">
                                                            <td className="px-4 py-3">
                                                                <div className="font-bold text-gray-900 dark:text-white">Injeção SCEE - Energia</div>
                                                                <div className="text-xs text-gray-400">Com percentual de desconto</div>
                                                            </td>
                                                            <td className="px-4 py-2 text-right">
                                                                <input
                                                                    type="number" step="0.01"
                                                                    value={edited.qtd_injecao_scee_boleto}
                                                                    onChange={e => handleChange('qtd_injecao_scee_boleto', Number(e.target.value))}
                                                                    className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2 text-right">
                                                                <input
                                                                    type="number" step="0.000001"
                                                                    value={edited.tarifa_injecao_gd1}
                                                                    onChange={e => handleChange('tarifa_injecao_gd1', Number(e.target.value))}
                                                                    className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                            <td className="bg-[#F8F9F7] px-4 py-2 text-right bg-white dark:bg-gray-800 font-medium">
                                                                {formatBRL(linha1_qtd * linha1_tarifa)}
                                                            </td>
                                                        </tr>

                                                        {/* LINHA 2 */}
                                                        <tr className="bg-white dark:bg-gray-800">
                                                            <td className="px-4 py-3">
                                                                <div className="font-bold text-gray-900 dark:text-white">Adicional de bandeira</div>
                                                                <div className="text-xs text-gray-400">Com percentual de desconto</div>
                                                            </td>
                                                            <td className="px-4 py-2 text-right">
                                                                <input
                                                                    type="number" step="0.01"
                                                                    value={edited.qtd_adc_bandeira_boleto}
                                                                    onChange={e => handleChange('qtd_adc_bandeira_boleto', Number(e.target.value))}
                                                                    className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2 text-right">
                                                                <input
                                                                    type="number" step="0.000001"
                                                                    value={Number(linha2_tarifa.toFixed(6))}
                                                                    onChange={e => {
                                                                        const val = Number(e.target.value);
                                                                        const original = desconto_bandeira_calc === 1 ? val : val / (1 - desconto_bandeira_calc);
                                                                        handleChange('tarifa_bandeira', original);
                                                                    }}
                                                                    className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2 text-right bg-white dark:bg-gray-800 font-medium">
                                                                {formatBRL(linha2_qtd * linha2_tarifa)}
                                                            </td>
                                                        </tr>

                                                        {/* LINHA 3 */}
                                                        <tr className="bg-[#F8F9F7] dark:bg-gray-900/50">
                                                            <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Parc. injet. s/ desconto</td>
                                                            <td className="px-4 py-2 text-right">
                                                                <input
                                                                    type="number" step="0.01"
                                                                    value={edited.qtd_parc_injet_boleto}
                                                                    onChange={e => handleChange('qtd_parc_injet_boleto', Number(e.target.value))}
                                                                    className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2 text-right">
                                                                <input
                                                                    type="number" step="0.000001"
                                                                    value={edited.tarifa_parc_injet_s_desc_gd2}
                                                                    onChange={e => handleChange('tarifa_parc_injet_s_desc_gd2', Number(e.target.value))}
                                                                    className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                            <td className="bg-[#F8F9F7] px-4 py-2 text-right bg-white dark:bg-gray-800 font-medium">
                                                                {formatBRL(-(linha5_qtd * linha5_tarifa))}
                                                            </td>
                                                        </tr>

                                                        {/* LINHA 4 */}
                                                        <tr className="bg-white dark:bg-gray-800">
                                                            <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Restituição de Imposto</td>
                                                            <td className="px-4 py-2 text-right"></td>
                                                            <td className="px-4 py-2 text-right"></td>
                                                            <td className="px-4 py-2 text-right bg-white dark:bg-gray-800">
                                                                <input
                                                                    type="number" step="0.01"
                                                                    value={edited.valor_restituicao_imposto_boleto.toFixed(2)}
                                                                    onChange={e => handleChange('valor_restituicao_imposto_boleto', Number(e.target.value))}
                                                                    className="w-28 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm font-medium focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                        </tr>

                                                        {/* LINHA 5 */}
                                                        <tr className="bg-[#F8F9F7] dark:bg-gray-900/50">
                                                            <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Energia compensada não isenta (TRIBUTOS) - UC</td>
                                                            <td className="px-4 py-2 text-right"></td>
                                                            <td className="px-4 py-2 text-right"></td>
                                                            <td className="bg-[#F8F9F7] px-4 py-2 text-right bg-white dark:bg-gray-800">
                                                                <input
                                                                    type="number" step="0.01"
                                                                    value={edited.valor_energia_nao_isenta_boleto.toFixed(2)}
                                                                    onChange={e => handleChange('valor_energia_nao_isenta_boleto', Number(e.target.value))}
                                                                    className=" w-28 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm font-medium focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                        </tr>

                                                        {/* LINHA 6 */}
                                                        <tr className="bg-white dark:bg-gray-800">
                                                            <td className="px-4 py-3 align-top">
                                                                <div className="font-bold text-gray-900 dark:text-white mb-2">Multas / Juros / Outros</div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[10px] text-gray-500 font-semibold mb-0.5">JUROS</span>
                                                                        <input
                                                                            type="number" step="0.01"
                                                                            value={edited.valor_juros_boleto}
                                                                            onChange={e => handleChange('valor_juros_boleto', Number(e.target.value))}
                                                                            className="w-20 text-right bg-gray-50 border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                        />
                                                                    </div>
                                                                    <span className="text-gray-300 mt-4">+</span>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[10px] text-gray-500 font-semibold mb-0.5">MULTAS/OUTROS</span>
                                                                        <input
                                                                            type="number" step="0.01"
                                                                            value={edited.outros_multas_boleto}
                                                                            onChange={e => handleChange('outros_multas_boleto', Number(e.target.value))}
                                                                            className="w-20 text-right bg-gray-50 border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col flex-1 pl-2 border-l border-gray-200 dark:border-gray-700">
                                                                        <span className="text-[10px] text-gray-500 font-semibold mb-0.5">DESCRIÇÃO (Opcional)</span>
                                                                        <input
                                                                            type="text"
                                                                            value={edited.outros_multas_descricao}
                                                                            onChange={e => handleChange('outros_multas_descricao', e.target.value)}
                                                                            placeholder="Ex: Multa por atraso..."
                                                                            className="w-full bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2 text-right"></td>
                                                            <td className="px-4 py-2 text-right"></td>
                                                            <td className="px-4 py-2 text-right bg-white dark:bg-gray-800 font-bold text-primary align-bottom">
                                                                {formatBRL(linha6_outros)}
                                                            </td>
                                                        </tr>

                                                        <tr className="bg-[#F8F9F7] dark:bg-gray-900/50">
                                                            <td className="px-4 py-3 align-top">
                                                                <div className="font-bold text-gray-900 dark:text-white mb-2">Parcelamentos</div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex flex-col flex-1">
                                                                        <span className="text-[10px] text-gray-500 font-semibold mb-0.5">DESCRIÇÃO (Opcional)</span>
                                                                        <input
                                                                            type="text"
                                                                            value={edited.parcelamento_descricao}
                                                                            onChange={e => handleChange('parcelamento_descricao', e.target.value)}
                                                                            placeholder="Ex: Parcelamento referente a..."
                                                                            className="w-full bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2 text-right"></td>
                                                            <td className="px-4 py-2 text-right"></td>
                                                            <td className="bg-[#F8F9F7] px-4 py-2 text-right bg-white dark:bg-gray-800 align-bottom">
                                                                <input
                                                                    type="number" step="0.01"
                                                                    value={edited.valor_parcelamentos_boleto.toFixed(2)}
                                                                    onChange={e => handleChange('valor_parcelamentos_boleto', Number(e.target.value))}
                                                                    className="w-28 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm font-medium focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                                />
                                                            </td>
                                                        </tr>

                                                        {/* OBSERVATIONS ROW */}
                                                        <tr className="bg-white dark:bg-gray-800">
                                                            <td className="px-4 py-3 align-top" colSpan={4}>
                                                                <div className="flex flex-col">
                                                                    <span className="text-[10px] text-gray-500 font-semibold mb-1 uppercase tracking-wide">Observações do Boleto (Impresso no PDF)</span>
                                                                    <textarea
                                                                        value={edited.observacoes_boleto}
                                                                        onChange={e => handleChange('observacoes_boleto', e.target.value)}
                                                                        placeholder="Observações que serão impressas no boleto..."
                                                                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700 min-h-[80px] resize-none"
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>

                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Footer */}
                        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-700 flex justify-end items-center gap-4 backdrop-blur-md shrink-0">

                            <div className="mr-auto flex flex-col items-start gap-2">

                                {/* Só no boleto único, que é o único caso em que a fatura da
                                    Equatorial está DENTRO do que vamos cobrar. Fora dele o
                                    cliente sempre pagou a distribuidora por conta própria e a
                                    opção não teria significado. */}
                                {boletoUnico && valorEquatorial > 0 && (
                                    <label
                                        className="flex items-start gap-2 cursor-pointer select-none max-w-xl"
                                        title="Marque quando o cliente já tiver pago o boleto da própria Equatorial. Sem isso, o boleto unificado cobraria a distribuidora uma segunda vez."
                                    >
                                        <input
                                            type="checkbox"
                                            checked={equatorialQuitada}
                                            onChange={e => setEquatorialQuitada(e.target.checked)}
                                            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                                        />
                                        <span className={`text-sm font-medium ${equatorialQuitada ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                            <span className="flex items-center gap-1.5">
                                                <Banknote size={15} /> Cliente já quitou a Equatorial (R$ {formatBRL(valorEquatorial)})
                                            </span>
                                            <span className="block text-xs font-normal mt-0.5 text-gray-500 dark:text-gray-400">
                                                {excluirEquatorial
                                                    ? `Boleto de R$ ${formatBRL(valorEmissaoBoletoUnico)}, só a energia. Cai na conta da Vigor, que repassa a parte da ${empresaParceira?.nome || 'parceira'}; não há Equatorial a pagar.`
                                                    : `Boleto unificado de R$ ${formatBRL(valorEmissaoBoletoUnico)} — Vigor + a fatura da Equatorial.`}
                                            </span>
                                            {/* Por que a opção já veio marcada — sem isto o operador
                                                acha que alguém marcou por engano e desmarca. */}
                                            {/* Pela política aprovada em 12/09/2026, UC em débito
                                                automático não entra no boleto único. Se a fatura
                                                chegou aqui assim, a UC escapou da trava de adesão:
                                                a parte da Equatorial fica fora de qualquer forma e
                                                o cadastro precisa ser corrigido. */}
                                            {emDebitoAutomatico && (
                                                <span className="block text-xs font-normal mt-1 text-red-600 dark:text-red-400 font-semibold">
                                                    Esta fatura está em DÉBITO AUTOMÁTICO — a Equatorial debita direto na conta do
                                                    cliente, então a parte dela fica fora deste boleto. Pela política aprovada, esta
                                                    UC não deveria estar no boleto único: revise a adesão em Financeiro &gt; UC.
                                                </span>
                                            )}
                                        </span>
                                    </label>
                                )}

                                {/* Emissão de teste: só adm. No caminho Asaas vai para o sandbox
                                    real do Asaas; no caminho Sicoob é simulado, porque o sandbox
                                    do Sicoob não registra boleto (só devolve mocks). */}
                                {profile?.role === 'adm' && (
                                    <label
                                        className={`flex items-center gap-2 select-none ${faturaDeTeste ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                        title={faturaDeTeste
                                            ? 'Esta fatura está marcada como teste e só emite em teste. Para cobrar de verdade, tire a marca de teste antes.'
                                            : 'Cria a cobrança na conta de testes do Asaas: nenhum dinheiro se move e a fatura fica fora de todos os relatórios.'}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={emitirEmSandbox}
                                            disabled={faturaDeTeste}
                                            onChange={e => setOptouPorSandbox(e.target.checked)}
                                            className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                                        />
                                        <span className={`text-sm font-medium flex items-center gap-1.5 ${emitirEmSandbox ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                            <FlaskConical size={15} /> Emitir em sandbox (teste)
                                        </span>
                                    </label>
                                )}
                            </div>

                            {/* UC cobrada por agrupamento: o boleto sai pelo Acompanhamento mensal da
                                Secretaria, nunca aqui. Antes isto só aparecia DEPOIS do clique em
                                Emitir, e o operador via um botão verde convidando ao erro. */}
                            {agrupamentoDaUc && (
                                <div className="flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 px-3 py-2 text-xs text-red-800 dark:text-red-300" role="alert">
                                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                                    <span>
                                        <strong>Esta UC não recebe boleto próprio.</strong> Ela é cobrada no boleto do
                                        agrupamento <strong>{agrupamentoDaUc}</strong>, junto com as outras unidades da
                                        Secretaria. Deixe a fatura calculada e emita em Financeiro → Agrupamentos de
                                        Faturamento → {agrupamentoDaUc} → Acompanhamento mensal. Para cobrar esta UC à
                                        parte, retire-a do agrupamento antes.
                                    </span>
                                </div>
                            )}

                            {/* Prazo em dia, mas a UC está num agrupamento ativo: avisa de onde vem a
                                data usada, para não parecer que a régua individual (vencimento - 7
                                dias) foi ignorada por acaso. */}
                            {!exigenciaJustificativa.exige && limiteAgrupamentoIso && (
                                <div className="flex items-center gap-2 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 px-3 py-2 text-xs text-sky-800 dark:text-sky-300">
                                    <CalendarClock size={15} className="shrink-0" />
                                    <span>
                                        UC em agrupamento de faturamento ativo: vale o prazo do contrato, até{' '}
                                        <strong>{formatDateString(exigenciaJustificativa.limite)}</strong> — não o vencimento da distribuidora.
                                    </span>
                                </div>
                            )}

                            {/* Aviso antecipado: quem chega aqui fora do prazo descobre
                                agora, e nao so depois de clicar em emitir. Nao bloqueia. */}
                            {exigenciaJustificativa.exige && (
                                <div className="flex items-center gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                                    <CalendarClock size={15} className="shrink-0" />
                                    <span>
                                        {motivoAtraso ? (
                                            <>Motivo do atraso registrado: <strong>{motivoAtraso}</strong></>
                                        ) : (
                                            <>
                                                Fora do prazo {limiteAgrupamentoIso ? 'do contrato do agrupamento' : 'padrão'} (terminou em{' '}
                                                {formatDateString(exigenciaJustificativa.limite)}) — será pedido o motivo.
                                            </>
                                        )}
                                    </span>
                                </div>
                            )}

                            <button
                                onClick={handleBack}
                                disabled={loading || !!boletoParado}
                                title={boletoParado ? 'Esta fatura já tem boleto emitido: leve-a para "A enviar" pelo aviso no topo.' : undefined}
                                className="disabled:cursor-not-allowed disabled:opacity-50 text-warning border border-warning hover:bg-warning/80 hover:text-white px-4 py-2.5 rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                            >
                                <ArrowLeft size={16} /> Retroceder
                            </button>

                            <button
                                onClick={() => {
                                    // Fora do prazo e ainda sem motivo: coleta primeiro,
                                    // depois segue para a conferência final de sempre.
                                    if (exigenciaJustificativa.exige && !motivoAtraso) {
                                        setShowJustificativaAtraso(true);
                                        return;
                                    }
                                    setShowEmitConfirm(true);
                                }}
                                disabled={loading || !!agrupamentoDaUc || !!boletoParado}
                                title={boletoParado
                                    ? `Esta fatura já tem o boleto ${String(boletoParado.num_cobranca_boleto).trim()} emitido: leve-a para "A enviar" pelo aviso no topo.`
                                    : agrupamentoDaUc
                                        ? `Esta UC é cobrada pelo boleto do agrupamento "${agrupamentoDaUc}".`
                                        : undefined}
                                className={`text-white px-6 py-2.5 rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50 ${emitirEmSandbox
                                    ? 'bg-amber-500 shadow-amber-500/30 hover:shadow-amber-500/40'
                                    : 'bg-teal-600 shadow-teal-500/30 hover:shadow-teal-500/40'}`}
                            >
                                {loading ? 'Enviando...' : (
                                    <>
                                        {emitirEmSandbox ? <FlaskConical size={18} /> : <Ticket size={18} />}
                                        {emitirEmSandbox ? 'Emitir teste' : 'Emitir boleto'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <ConfirmationModal
                isOpen={!!pendingConflito}
                onClose={() => setPendingConflito(null)}
                onConfirm={() => {
                    // Pessoas diferentes no mesmo CPF/CNPJ: só admin libera (#6155). Para os demais
                    // o botão só fecha — o caminho é corrigir o cadastro.
                    if (faltaRessalva) return;
                    ressalvaAceitaRef.current = pendingConflito?.pessoaDiferente ? ressalvaPagador.trim() : null;
                    conflitoLiberadoRef.current = true;
                    setPendingConflito(null);
                    executeEmitir();
                }}
                title={pendingConflito?.pessoaDiferente ? "Atenção: este CPF/CNPJ parece de outra pessoa" : "ATENÇÃO: CPF/CNPJ já usado com outro nome"}
                description={
                    <ConflitoPagadorAviso
                        conflito={pendingConflito}
                        nomeEmitido={edited.nome_cliente_boleto}
                        ressalva={ressalvaPagador}
                        onRessalvaChange={setRessalvaPagador}
                        onUsarNomeOficial={(nome) => {
                            // Fecha o aviso: o próximo "Emitir" confere de novo, já com o nome oficial.
                            setEdited(prev => ({ ...prev, nome_cliente_boleto: nome }));
                            setPendingConflito(null);
                        }}
                    />
                }
                confirmText={pendingConflito?.pessoaDiferente ? "Emitir mesmo assim" : "É o mesmo cliente, emitir"}
                confirmDisabled={faltaRessalva}
                cancelText="Cancelar e conferir o cadastro"
                variant={pendingConflito?.pessoaDiferente ? "danger" : "warning"}
            />

            <ConfirmationModal
                isOpen={showUcConfirmModal}
                onClose={() => setShowUcConfirmModal(false)}
                onConfirm={() => {
                    setShowUcConfirmModal(false);
                    setShouldSaveUc(true);
                    executeEmitir();
                }}
                title="ATENÇÃO: UC Não Encontrada"
                description={
                    <div className="text-left">
                        <p className="mb-2">
                            A Unidade Consumidora (UC) <strong>{edited.uc_codigo}</strong> não existe no banco de dados.
                        </p>
                        <p className="mb-2">
                            Se este número estiver incorreto, a cobrança não será identificada como duplicada no futuro.
                        </p>
                        <p>
                            Você tem certeza de que o número está correto e deseja registrar esta UC?
                        </p>
                    </div>
                }
                confirmText="Sim, criar nova UC"
                cancelText="Não, vou corrigir o número"
                variant="warning"
            />

            <ConfirmationModal
                isOpen={showEmitConfirm}
                onClose={() => setShowEmitConfirm(false)}
                onConfirm={() => {
                    setShowEmitConfirm(false);
                    handleEmitir();
                }}
                title={emitirEmSandbox ? 'Confirmar Emissão de TESTE (sandbox)' : 'Confirmar Emissão de Boleto'}
                description={
                    <div className="text-left mt-3 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        {emitirEmSandbox && (
                            <div className="mb-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                                <p className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                                    <FlaskConical size={15} className="shrink-0 mt-0.5" />
                                    <span>
                                        {bancoGateway === 'asaas' ? (
                                            <>
                                                <strong>Cobrança de teste.</strong> Vai para a conta sandbox do Asaas: nenhum dinheiro se move,
                                                o cliente não recebe nada e a fatura fica marcada como sandbox — fora de somatórios,
                                                inadimplência e exportações. Cancelar a cobrança não tira a marca: a fatura
                                                continua de teste até alguém tirar a marca na lista.
                                            </>
                                        ) : (
                                            <>
                                                <strong>Boleto de teste simulado.</strong> O Sicoob não tem ambiente de homologação que registre
                                                boleto, então nada é enviado ao banco: a fatura recebe nosso número, linha digitável e Pix
                                                fictícios (não pagáveis), o PDF sai com marca d'água TESTE e a fatura fica marcada como
                                                sandbox — fora de somatórios, inadimplência e exportações.
                                            </>
                                        )}
                                    </span>
                                </p>
                            </div>
                        )}
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">Por favor, confira os dados abaixo:</p>
                        <ul className="text-sm space-y-2">
                            <li className="flex justify-between border-b pb-1 dark:border-gray-700">
                                <span className="font-semibold text-gray-500">Conta:</span>
                                <span className={`font-bold text-right ${COR_TEXTO_CONTA[contaEmissao]}`}>
                                    {NOME_CONTA_SICOOB[contaEmissao]}
                                </span>
                            </li>
                            <li className="flex justify-between border-b pb-1 dark:border-gray-700">
                                <span className="font-semibold text-gray-500">UC:</span>
                                <span className="font-medium text-gray-900 dark:text-white text-right max-w-[500px] truncate" title={edited.uc_codigo}>{edited.uc_codigo}</span>
                            </li>
                            <li className="flex justify-between border-b pb-1 dark:border-gray-700">
                                <span className="font-semibold text-gray-500">Cliente:</span>
                                <span className="font-medium text-gray-900 dark:text-white text-right max-w-[500px] truncate" title={edited.nome_cliente_boleto}>{edited.nome_cliente_boleto}</span>
                            </li>
                            <li className="flex justify-between border-b pb-1 dark:border-gray-700">
                                <span className="font-semibold text-gray-500">CPF/CNPJ:</span>
                                <span className="font-medium text-gray-900 dark:text-white text-right max-w-[500px] truncate" title={edited.cpf_cnpj_cliente_boleto}>{edited.cpf_cnpj_cliente_boleto}</span>
                            </li>
                            {/* Fora do boleto único o valor cobrado É a parte da Vigor, e uma
                                linha só basta. No boleto único as duas parcelas são exibidas
                                separadas: a confirmação mostrava só a parte da Vigor, e quem
                                emitia não via o valor que o cliente ia de fato pagar. */}
                            {boletoUnico ? (
                                <>
                                    <li className="flex justify-between border-b pb-1 dark:border-gray-700">
                                        <span className="font-semibold text-gray-500">Parte da Vigor:</span>
                                        <span className="font-medium text-gray-900 dark:text-white text-right">R$ {formatBRL(edited.valor_boleto_vigor)}</span>
                                    </li>
                                    <li className="flex justify-between border-b pb-1 dark:border-gray-700">
                                        <span className="font-semibold text-gray-500">Fatura da Equatorial:</span>
                                        <span className={`font-medium text-right ${excluirEquatorial ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white'}`}>
                                            {excluirEquatorial
                                                ? `${motivoExclusaoEquatorial} — fora do boleto`
                                                : `R$ ${formatBRL(valorEquatorial)}`}
                                            {!excluirEquatorial && corrigeValorEquatorial && (
                                                <span className="block text-[11px] font-normal text-amber-700 dark:text-amber-400">
                                                    código de barras da conta; a fatura tinha R$ {formatBRL(valorGravadoEquatorial)} (soma das parcelas)
                                                </span>
                                            )}
                                        </span>
                                    </li>
                                    <li className="flex justify-between pt-1">
                                        <span className="font-semibold text-gray-500">Valor do boleto:</span>
                                        <span className="font-bold text-primary text-right">R$ {formatBRL(valorEmissaoBoletoUnico)}</span>
                                    </li>
                                </>
                            ) : (
                                <li className="flex justify-between pt-1">
                                    <span className="font-semibold text-gray-500">Valor Total:</span>
                                    <span className="font-bold text-primary text-right">R$ {formatBRL(edited.valor_boleto_vigor)}</span>
                                </li>
                            )}
                        </ul>
                        {splitPreview && empresaParceira && (
                            <div className={`mt-4 p-3 rounded-lg border text-xs ${splitPreview.status === 'ok' || splitPreview.status === 'sem_repasse'
                                ? 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700'
                                : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/50'}`}>
                                <p className="font-bold text-gray-700 dark:text-gray-200 mb-2">Divisão do pagamento</p>
                                {splitPreview.status === 'ok' || splitPreview.status === 'sem_repasse' ? (
                                    <>
                                        <div className="flex justify-between py-0.5">
                                            <span className="text-gray-500">Vigor ({formatBRL(percentualVigorUc ?? 0)}% da tarifa cheia):</span>
                                            <span className="font-semibold text-gray-900 dark:text-white">R$ {formatBRL(splitPreview.valorVigor)}</span>
                                        </div>
                                        <div className="flex justify-between py-0.5">
                                            <span className="text-gray-500">{empresaParceira.nome} (repasse por split):</span>
                                            <span className="font-semibold text-gray-900 dark:text-white">R$ {formatBRL(splitPreview.valorParceira)}</span>
                                        </div>
                                        {/* Desde 26/09/2026 a Vigor recebe tudo e paga a Equatorial. */}
                                        {valorEquatorial > 0 && boletoUnico && (
                                            <div className="flex justify-between py-0.5">
                                                <span className="text-gray-500">
                                                    {excluirEquatorial
                                                        ? `Equatorial (${motivoExclusaoEquatorial}):`
                                                        : 'Equatorial (a Vigor paga a conta):'}
                                                </span>
                                                <span className="font-semibold text-gray-900 dark:text-white">
                                                    R$ {formatBRL(excluirEquatorial ? 0 : valorEquatorial)}
                                                </span>
                                            </div>
                                        )}
                                        {/* O boleto é emitido na conta da Vigor: o que vai no split é o
                                            dinheiro da parceira, e a taxa do Asaas sai dessa parte. */}
                                        <p className="mt-2 text-gray-500">
                                            {splitPreview.status === 'sem_repasse'
                                                ? `Nada a repassar à ${empresaParceira.nome} nesta fatura (sem energia injetada, ou desconto igual ao deságio da UC). O boleto cai inteiro na conta da Vigor.`
                                                : `O dinheiro cai na conta Asaas da Vigor. A ${empresaParceira.nome} recebe a parte dela por split, menos a taxa do Asaas.`}
                                        </p>
                                    </>
                                ) : (
                                    <div className="flex items-start gap-2 text-red-800 dark:text-red-400">
                                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                        <div>
                                            <strong className="font-bold">A emissão será recusada:</strong>
                                            <p className="mt-1">{splitPreview.motivo}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Antes este aviso pedia para marcar "cliente já quitou" e evitar a
                            cobrança em dobro. Com a política de 12/09/2026 a exclusão passou a ser
                            automática (`excluirEquatorial`), então o que sobra não é risco de
                            cobrança: é uma UC cadastrada de um jeito que a política não permite. */}
                        {boletoUnico && emDebitoAutomatico && (
                            <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 rounded-lg text-xs text-red-800 dark:text-red-400 flex items-start gap-2">
                                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                <div>
                                    <strong className="font-bold">Esta UC não deveria estar no boleto único:</strong>
                                    <p className="mt-1">
                                        A fatura está em <strong>débito automático</strong> e a Equatorial vai debitar
                                        R$ {formatBRL(valorEquatorial)} direto da conta do cliente. A parte dela já foi tirada
                                        deste boleto, então não há cobrança em dobro — mas, pela política aprovada, só entra no
                                        boleto único quem cancelou o débito automático. Revise a adesão em Financeiro &gt; UC.
                                    </p>
                                </div>
                            </div>
                        )}
                        {/* O motivo do atraso entra na conferencia final: quem confirma
                            ve o que vai ficar registrado, e ainda da tempo de trocar. */}
                        {exigenciaJustificativa.exige && motivoAtraso && (
                            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-300">
                                <div className="flex items-start gap-2">
                                    <CalendarClock size={16} className="shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <strong className="font-bold">Emissão fora do prazo padrão</strong>
                                        <p className="mt-1"><span className="font-semibold">Motivo:</span> {motivoAtraso}</p>
                                        {justificativaAtraso.trim() && (
                                            <p className="mt-0.5 break-words"><span className="font-semibold">Justificativa:</span> {justificativaAtraso.trim()}</p>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => { setShowEmitConfirm(false); setShowJustificativaAtraso(true); }}
                                            className="mt-1.5 underline font-semibold hover:no-underline"
                                        >
                                            Alterar motivo
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {!ucRegistered && (
                            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg text-xs text-yellow-800 dark:text-yellow-400 flex items-start gap-2">
                                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                <div>
                                    <strong className="font-bold">Atenção:</strong> Esta UC não está cadastrada no banco de dados.
                                    <p className="mt-1">Ao confirmar, o sistema solicitará permissão para registrar esta UC.</p>
                                </div>
                            </div>
                        )}
                    </div>
                }
                confirmText="Confirmar"
                cancelText="Revisar"
                variant="info"
            />

            {exigenciaJustificativa.exige && (
                <JustificativaAtrasoModal
                    isOpen={showJustificativaAtraso}
                    onClose={() => setShowJustificativaAtraso(false)}
                    onConfirm={({ motivo, justificativa }) => {
                        setMotivoAtraso(motivo);
                        setJustificativaAtraso(justificativa);
                        setShowJustificativaAtraso(false);
                        setShowEmitConfirm(true);
                    }}
                    limite={exigenciaJustificativa.limite}
                    diasDeAtraso={exigenciaJustificativa.diasDeAtraso}
                    dataVencimento={fatura?.data_vencimento}
                    motivoInicial={motivoAtraso}
                    justificativaInicial={justificativaAtraso}
                />
            )}
        </>
    );
};

export default BoletoEmissionModal;
